import { api, ensureUiScope } from './api';

/** Safety Report / evidence pack builds compose several heavy reports; large windows can exceed 3 minutes. */
export const EVIDENCE_PACK_REQUEST_TIMEOUT_MS = 600_000;
/** Sign/finalize freezes the Signed Record only. Packages/PDF are lazy and must not use the 180s pack timeout. */
export const SAFETY_REPORT_FINALIZE_TIMEOUT_MS = 30_000;

export interface IncidentsRollupFilters {
  from_date: string; // ISO
  to_date: string; // ISO
  org_id?: string;
  site_id?: string;
  device_id?: string;
  zone_id?: string;
  camera_ids?: string[];
  policy_ids?: string[];
  policy_type?: string;
}

export type ReportVisualType =
  | 'scorecard'
  | 'trend_line'
  | 'severity_mix'
  | 'top_n_bar'
  | 'stacked_bar'
  | 'readiness_matrix'
  | 'timeline'
  | 'manifest_status';

export interface ReportVisual {
  id: string;
  title: string;
  type: ReportVisualType;
  insight: string;
  data: Record<string, any>;
  empty_state?: {
    title: string;
    detail?: string;
    missing_data?: string[];
  };
  links?: Array<{
    label: string;
    href: string;
  }>;
}

export interface IncidentsRollupResponse {
  ok: boolean;
  error?: string;
  filters?: any;
  totals?: {
    total: number;
    by_severity: { low: number; medium: number; high: number; critical: number };
  };
  violations_by_zone_camera_hour?: Array<{
    zone_id: string;
    camera_id: string;
    hour_of_day: number;
    count: number;
  }>;
  top_hotspots_by_zone?: Array<{ zone_id: string; count: number; max_severity?: string | null }>;
  operator_summary?: {
    top_problem_areas?: Array<{
      zone_id: string;
      camera_id: string;
      device_id?: string;
      count: number;
      max_severity?: string | null;
      risk_score?: number;
      dominant_bucket?: 'safety_critical' | 'operational' | 'other' | string;
      trend_vs_previous?: {
        current_count?: number;
        previous_count?: number;
        trend_ratio?: number;
      };
      bucket_counts?: {
        safety_critical?: number;
        operational?: number;
        other?: number;
        suppressed_or_ignored?: number;
      };
      links?: {
        incidents?: string;
        live_view?: string;
        device_control?: string;
      };
    }>;
    priority_ranked_areas?: Array<{
      zone_id: string;
      camera_id: string;
      device_id?: string;
      count: number;
      max_severity?: string | null;
      risk_score?: number;
      dominant_bucket?: 'safety_critical' | 'operational' | 'other' | string;
      trend_vs_previous?: {
        current_count?: number;
        previous_count?: number;
        trend_ratio?: number;
      };
      bucket_counts?: {
        safety_critical?: number;
        operational?: number;
        other?: number;
        suppressed_or_ignored?: number;
      };
      links?: {
        incidents?: string;
        live_view?: string;
        device_control?: string;
      };
    }>;
    top_problem_types?: Array<{
      incident_type: string;
      count: number;
      max_severity?: string | null;
      signal_bucket?: 'safety_critical' | 'operational' | 'other' | string;
      signal_score?: number;
      links?: {
        incidents?: string;
      };
    }>;
    top_problem_types_by_count?: Array<{
      incident_type: string;
      count: number;
      max_severity?: string | null;
      links?: {
        incidents?: string;
      };
    }>;
    problem_type_views?: {
      ranked_problem_types?: Array<{
        incident_type: string;
        count: number;
        max_severity?: string | null;
        signal_bucket?: 'safety_critical' | 'operational' | 'other' | string;
        signal_score?: number;
        links?: { incidents?: string };
      }>;
      safety_critical?: Array<{
        incident_type: string;
        count: number;
        links?: { incidents?: string };
      }>;
      operational?: Array<{
        incident_type: string;
        count: number;
        links?: { incidents?: string };
      }>;
      other?: Array<{
        incident_type: string;
        count: number;
        links?: { incidents?: string };
      }>;
    };
    noise_signal?: {
      safety_critical?: {
        count: number;
        pct_of_total?: number;
        top_types?: Array<{
          incident_type: string;
          count: number;
          links?: { incidents?: string };
        }>;
        links?: {
          incidents?: string;
        };
      };
      operational?: {
        count: number;
        pct_of_total?: number;
        top_types?: Array<{
          incident_type: string;
          count: number;
          links?: { incidents?: string };
        }>;
        links?: {
          incidents?: string;
        };
      };
      other?: {
        count: number;
        pct_of_total?: number;
        top_types?: Array<{
          incident_type: string;
          count: number;
          links?: { incidents?: string };
        }>;
        links?: {
          incidents?: string;
        };
      };
      suppressed_or_ignored?: {
        count: number;
        suppressed_count?: number;
        ignored_count?: number;
        mode?: string;
        pct_of_total?: number;
        links?: {
          incidents?: string;
        };
      };
      dominant_bucket?: 'safety_critical' | 'operational' | 'other' | 'none' | string;
    };
    since_last_report?: {
      current_total: number;
      previous_total: number;
      delta_count: number;
      delta_pct: number;
      direction: 'up' | 'down' | 'flat' | string;
      previous_from_date?: string;
      previous_to_date?: string;
    };
    risk_flags?: Array<{
      level: 'red' | 'amber' | 'grey' | string;
      title: string;
      detail?: string;
      metric?: string;
      value?: number;
      threshold?: number;
      links?: {
        incidents?: string;
        live_view?: string;
        device_control?: string;
      };
    }>;
    suppression_transparency?: {
      mode?: string;
      active_rules?: number;
      by_status?: Array<{
        status: string;
        count: number;
      }>;
      top_reasons?: Array<{
        reason: string;
        count: number;
      }>;
      top_scopes?: Array<{
        zone_id?: string;
        camera_id?: string;
        device_id?: string;
        incident_type?: string;
        count?: number;
        links?: {
          incidents?: string;
          live_view?: string;
          device_control?: string;
        };
      }>;
      error?: string;
    };
    top_actions?: Array<{
      title: string;
      detail?: string;
      priority?: 'high' | 'medium' | 'low' | string;
      links?: {
        incidents?: string;
        live_view?: string;
        device_control?: string;
      };
    }>;
    policy_meta?: {
      version?: number;
      from_db?: boolean;
      limits?: {
        top_problem_areas?: number;
        top_problem_types?: number;
        top_actions?: number;
      };
      thresholds?: {
        ppe_red_per_hour_by_zone?: number;
        dwell_amber_trend_multiplier?: number;
        high_critical_red_count?: number;
      };
      classification?: {
        safety_critical_types?: string[];
        operational_types?: string[];
      };
      must_act_incident_types?: string[];
      scoring_model?: {
        priority_ranked_areas?: {
          frequency_weight?: number;
          severity_weight?: number;
          safety_ratio_weight?: number;
          trend_weight?: number;
          suppression_penalty_weight?: number;
          score_range?: number[];
        };
        ranked_problem_types?: {
          frequency_weight?: number;
          severity_weight?: number;
          bucket_weight?: number;
          must_act_bonus?: number;
          score_range?: number[];
        };
      };
    };
  };
  recent_incidents?: Array<any>;
  incident_url_base?: string | null;
  report_visuals?: ReportVisual[];
}

function _buildRollupQuery(params: IncidentsRollupFilters): string {
  const q = new URLSearchParams();
  q.set('from_date', params.from_date);
  q.set('to_date', params.to_date);
  if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
  if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
  if (params.device_id) q.set('device_id', String(params.device_id || '').trim());
  if (params.zone_id) q.set('zone_id', params.zone_id);
  for (const cam of params.camera_ids || []) {
    const v = String(cam || '').trim();
    if (v) q.append('camera_id', v);
  }
  for (const pid of params.policy_ids || []) {
    const v = String(pid || '').trim();
    if (v) q.append('policy_id', v);
  }
  if (params.policy_type) q.set('policy_type', String(params.policy_type || '').trim());
  return q.toString();
}

function _filenameFromContentDisposition(cdRaw: any, fallback: string): string {
  const cd = String(cdRaw || '');
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return (m && m[1]) || fallback;
}

function _buildCraFormData(input: CraReadinessRunInput): FormData {
  const fd = new FormData();
  if (input.task) fd.append('task', String(input.task || '').trim());
  if (input.camera_view_id) fd.append('camera_view_id', String(input.camera_view_id || '').trim());
  if (input.source) fd.append('source', String(input.source || '').trim());
  if (input.edge_device_id) fd.append('edge_device_id', String(input.edge_device_id || '').trim());
  if (input.include_edge_frames_zip != null) {
    fd.append('include_edge_frames_zip', input.include_edge_frames_zip ? '1' : '0');
  }
  if (input.camera_id) fd.append('camera_id', String(input.camera_id || '').trim());
  if (input.capture_sec != null && Number.isFinite(input.capture_sec)) {
    fd.append('capture_sec', String(Math.floor(Number(input.capture_sec))));
  }
  if (input.duration_sec != null && Number.isFinite(input.duration_sec)) {
    fd.append('duration_sec', String(Math.floor(Number(input.duration_sec))));
  }
  if (input.clipFile) fd.append('clip_file', input.clipFile);
  if (input.precomputedFile) fd.append('precomputed_file', input.precomputedFile);
  if (input.roiFile) fd.append('roi_file', input.roiFile);
  if (input.roiJson) fd.append('roi_json', JSON.stringify(input.roiJson));
  if (input.deviceMetadataFile) fd.append('device_metadata_file', input.deviceMetadataFile);
  if (input.deviceMetadataJsonInline) {
    fd.append('device_metadata_json_inline', JSON.stringify(input.deviceMetadataJsonInline));
  }
  if (input.acceptance_mode != null) fd.append('acceptance_mode', input.acceptance_mode ? '1' : '0');
  if (input.captureProtocolFile) fd.append('capture_protocol_file', input.captureProtocolFile);
  if (input.captureProtocolJsonInline) {
    fd.append('capture_protocol_json_inline', JSON.stringify(input.captureProtocolJsonInline));
  }
  if (input.capture_scenario) fd.append('capture_scenario', String(input.capture_scenario || '').trim());
  if (input.annotate_frames != null) fd.append('annotate_frames', input.annotate_frames ? '1' : '0');
  if (input.include_raw_frames_json != null) {
    fd.append('include_raw_frames_json', input.include_raw_frames_json ? '1' : '0');
  }
  if (input.analysis_fps != null && Number.isFinite(input.analysis_fps)) {
    fd.append('analysis_fps', String(Number(input.analysis_fps)));
  }
  if (input.max_duration_sec != null && Number.isFinite(input.max_duration_sec)) {
    fd.append('max_duration_sec', String(Math.floor(Number(input.max_duration_sec))));
  }
  if (input.max_frames != null && Number.isFinite(input.max_frames)) {
    fd.append('max_frames', String(Math.floor(Number(input.max_frames))));
  }
  return fd;
}

function _buildSiteReadinessFormData(input: SiteReadinessRunInput): FormData {
  const fd = new FormData();
  if (input.siteProfileFile) fd.append('site_profile_file', input.siteProfileFile);
  if (input.camerasFile) fd.append('cameras_file', input.camerasFile);
  if (input.site_profile_path) fd.append('site_profile_path', String(input.site_profile_path || '').trim());
  if (input.cameras_path) fd.append('cameras_path', String(input.cameras_path || '').trim());
  if (input.mode) fd.append('mode', String(input.mode || 'install').trim());
  if (input.task_profile) fd.append('task_profile', String(input.task_profile || '').trim());
  if (input.rtsp_stability_sec != null && Number.isFinite(input.rtsp_stability_sec)) {
    fd.append('rtsp_stability_sec', String(Math.floor(Number(input.rtsp_stability_sec))));
  }
  if (input.rtsp_jitter_fail_ms != null && Number.isFinite(input.rtsp_jitter_fail_ms)) {
    fd.append('rtsp_jitter_fail_ms', String(Number(input.rtsp_jitter_fail_ms)));
  }
  if (input.switch_model) fd.append('switch_model', String(input.switch_model || '').trim());
  if (input.poe_total_watts != null && Number.isFinite(input.poe_total_watts)) {
    fd.append('poe_total_watts', String(Number(input.poe_total_watts)));
  }
  if (input.poe_port_class) fd.append('poe_port_class', String(input.poe_port_class || '').trim());
  if (input.poe_ports != null && Number.isFinite(input.poe_ports)) {
    fd.append('poe_ports', String(Math.floor(Number(input.poe_ports))));
  }
  if (input.powered_cameras != null && Number.isFinite(input.powered_cameras)) {
    fd.append('powered_cameras', String(Math.floor(Number(input.powered_cameras))));
  }
  if (input.cameras_transport) fd.append('cameras_transport', String(input.cameras_transport || '').trim());
  if (input.rf_rssi_db != null && Number.isFinite(input.rf_rssi_db)) {
    fd.append('rf_rssi_db', String(Number(input.rf_rssi_db)));
  }
  if (input.rf_channel != null && Number.isFinite(input.rf_channel)) {
    fd.append('rf_channel', String(Math.floor(Number(input.rf_channel))));
  }
  if (input.rf_packet_loss_pct != null && Number.isFinite(input.rf_packet_loss_pct)) {
    fd.append('rf_packet_loss_pct', String(Number(input.rf_packet_loss_pct)));
  }
  if (input.rf_measurement_method) fd.append('rf_measurement_method', String(input.rf_measurement_method || '').trim());
  return fd;
}

export interface ReliabilityBundleFilters {
  start_ts: string; // ISO
  end_ts: string; // ISO
  org_id?: string;
  site_id?: string;
  scope?: 'site' | 'device' | 'camera' | 'zone';
  scope_id?: string;
  format?: 'pdf' | 'csv' | 'json';
}

export interface ReliabilityProofPackFilters {
  start_ts: string; // ISO
  end_ts: string; // ISO
  org_id?: string;
  site_id?: string;
  device_id?: string;
  zone_id?: string;
  camera_ids?: string[];
  policy_ids?: string[];
  policy_type?: string;
  case_id?: string;
  incident_id?: string;
  format?: 'zip' | 'pdf' | 'json';
}

export interface EvidencePackFilters {
  from_date?: string; // ISO
  to_date?: string; // ISO
  org_id?: string;
  site_id?: string;
  device_id?: string;
  zone_id?: string;
  camera_ids?: string[];
  policy_ids?: string[];
  policy_type?: string;
  case_id?: string;
  incident_id?: string;
  report_id?: string;
  /** Epic 91 — hazard-class report mode (aggregates matching patterns in scope). */
  hazard_class?: string;
  /** When false, GET evidence_pack returns status only and never generates. */
  build?: boolean;
}

export interface TrustPackFilters {
  from_date: string; // ISO (used for window labeling)
  to_date: string; // ISO
  org_id?: string;
  site_id?: string;
}

export interface TrustPackBuildStartResponse {
  ok: boolean;
  job_id?: string;
  tag?: string;
  error?: string;
  detail?: string;
}

export interface TrustPackJobStatusResponse {
  ok: boolean;
  status?: 'running' | 'completed' | 'error' | 'cancelled' | string;
  job_id?: string;
  tag?: string;
  detail?: string;
  error?: string;
  started_at?: string;
  finished_at?: string;
  artifact?: {
    zip_path?: string;
    bytes?: number;
    filename?: string;
  };
}

export interface ReportClaim {
  claim_id: string;
  claim_type: string;
  claim_scope_type: 'anchor_case_claim' | 'report_scope_claim' | 'pattern_claim' | 'limitation_claim';
  customer_text: string;
  internal_text?: string | null;
  source_incident_ids: string[];
  source_safety_case_ids?: string[];
  proof_scope_ids: string[];
  evidence_ids?: string[];
  review_outcome?: string | null;
  evidence_readiness_state?: string | null;
  evidence_readiness_label?: string | null;
  action_state?: string | null;
  outcome_measurement_state?: string | null;
  source_type?: string | null;
  pattern_id?: string | null;
  group_key?: string | null;
  recurrence_source?: string | null;
  representative_incident_id?: string | null;
  representative_safety_case_id?: string | null;
  selected_window_signal_count?: number | null;
  lookback_signal_count?: number | null;
  lookback_active_days?: number | null;
  risk_disposition?: string | null;
  customer_risk_supported?: boolean | null;
  source_context?: string | null;
  pattern_source?: {
    pattern_id?: string | null;
    source_type?: 'incident_group' | string | null;
    recurrence_source?: 'incident_groups' | string | null;
    group_key?: string | null;
    representative_incident_id?: string | null;
    representative_safety_case_id?: string | null;
    selected_window_alert_count?: number | null;
    lookback_signal_count?: number | null;
    lookback_active_days?: number | null;
    pattern_state?: 'isolated' | 'emerging_pattern' | 'recurring_hotspot' | string | null;
    pattern_label?: string | null;
    pattern_summary?: string | null;
    review_priority?: 'low' | 'medium' | 'high' | string | null;
  } | null;
  customer_visible: boolean;
  confidence_boundary: string;
  limitations?: string[];
}

export interface ReportProjectionLabelBundle {
  internal_rule?: string | null;
  customer_rule?: string | null;
  customer_area?: string | null;
  policy_report_context?: PolicyReportContextPayload | null;
}

export interface ReportProjectionCounts {
  selected_incidents?: number;
  alerts_events_observed?: number;
  related_alerts_events?: number;
  accessible_snapshots?: number;
  accessible_clips?: number;
  missing_high_risk_clips?: number;
}

export interface ReportProjectionWindowContext {
  selected_report_window?: {
    from?: string | null;
    to?: string | null;
  };
  incident_group_duration?: {
    from?: string | null;
    to?: string | null;
  };
  included_incident_cases?: number;
  selected_window_alert_count?: number;
  recent_recurrence_alert_count?: number;
  recurrence_lookback_days?: number | null;
  recurrence_lookback_window?: {
    days?: number | null;
  };
  evidence_capture_window?: {
    from?: string | null;
    to?: string | null;
  };
}

export interface CustomerMetricSemantic {
  semantic_key: string;
  value: number;
  grain: string;
  population_key: string;
  window_kind: string;
  window_start?: string | null;
  window_end?: string | null;
  scope?: Record<string, string | null | undefined> | null;
  qualification?: Record<string, string | boolean | number | null | undefined> | null;
  review_state?: string | null;
  source?: string | null;
}

export interface ReportProjectionEvidenceBreakdown {
  readiness_state?: string | null;
  readiness_label?: string | null;
  snapshot_status?: string | null;
  clip_status?: string | null;
  meta_status?: string | null;
  accessible_snapshots?: number;
  accessible_clips?: number;
  clips_missing?: number | null;
  missing_high_risk_clips?: number;
  snapshot_coverage_rate?: number | null;
  clip_coverage_rate?: number | null;
  evidence_operator_status?: string | null;
  has_any_attachment?: boolean;
  timeline_available?: boolean;
  technical_appendix_available?: boolean;
}

export interface ReportProjectionWorkflowState {
  workflow_id?: string | null;
  safety_case_state?: string | null;
  safety_case_state_label?: string | null;
  claim_readiness?: Partial<WorkflowClaimReadinessResponse> | null;
  review?: {
    status?: string | null;
    status_label?: string | null;
    reviewed_by?: string | null;
    reviewed_at?: string | null;
    review_decision?: string | null;
    review_notes?: string | null;
    false_positive_reason?: string | null;
    severity_confirmed?: boolean | null;
    review_outcome?: string | null;
    review_outcome_label?: string | null;
    review_outcome_source?: string | null;
    review_outcome_source_label?: string | null;
  };
  ownership?: {
    owner?: string | null;
    rule_owner?: string | null;
    followup_owner?: string | null;
    assignee?: string | null;
    assignment_team?: string | null;
    assigned_team?: string | null;
    assigned_at?: string | null;
    has_owner?: boolean;
    has_followup_owner?: boolean;
  };
  corrective_action?: {
    state?: string | null;
    state_label?: string | null;
    work_order_id?: string | number | null;
    work_order_state?: string | null;
    has_work_order?: boolean;
    required?: boolean | null;
    status?: string | null;
    description?: string | null;
    due_date?: string | null;
    completion_evidence?: string | null;
  };
  corrective_actions?: {
    active_actions?: WorkflowCorrectiveAction[];
    completed_actions?: WorkflowCorrectiveAction[];
    cancelled_actions?: WorkflowCorrectiveAction[];
    evidence_summary?: WorkflowCorrectiveActionEvidenceSummary | null;
  };
  action_snapshot?: {
    state?: string | null;
    state_label?: string | null;
    owner?: string | null;
    next_review_at?: string | null;
    has_owner?: boolean;
  };
  outcome_measurement?: {
    state?: string | null;
    state_label?: string | null;
    baseline_available?: boolean;
    measurement_available?: boolean;
    baseline_window?: string | null;
    follow_up_window?: string | null;
    baseline_recurrence_count?: number | null;
    follow_up_recurrence_count?: number | null;
    risk_reduction_status?: string | null;
    risk_reduction_percent?: number | null;
    outcome_confidence?: string | null;
    measured_at?: string | null;
    outcome_status?: string | null;
    note?: string | null;
  };
  impact_summary?: WorkflowImpactSummary | null;
  escalation?: {
    state?: string | null;
    state_label?: string | null;
    reasons?: string[];
    next_review_at?: string | null;
    needs_attention?: boolean;
    overdue?: boolean;
    level?: number | null;
  };
}

export type WorkflowCorrectiveActionStatus =
  | 'planned'
  | 'started'
  | 'completed'
  | 'validated'
  | 'waived'
  | 'cancelled'
  | string;
export type WorkflowCorrectiveActionEvidenceType =
  | 'note'
  | 'photo'
  | 'video_clip'
  | 'document'
  | 'system_observation'
  | 'supervisor_signoff'
  | string;

export interface WorkflowCorrectiveAction {
  action_id?: string | null;
  org_id?: string | null;
  site_id?: string | null;
  workflow_id?: string | null;
  action_type?: string | null;
  title?: string | null;
  description?: string | null;
  priority?: string | null;
  assignee?: string | null;
  status?: WorkflowCorrectiveActionStatus;
  verification_method?: string | null;
  verification_notes?: string | null;
  safety_control_review?: SafetyControlReviewPayload | null;
  due_date?: string | null;
  completed_at?: string | null;
  validated_at?: string | null;
  implemented_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  evidence_count?: number;
  latest_evidence_at?: string | null;
}

export interface WorkflowCorrectiveActionEvidence {
  evidence_id?: string | null;
  action_id?: string | null;
  org_id?: string | null;
  site_id?: string | null;
  workflow_id?: string | null;
  evidence_type?: WorkflowCorrectiveActionEvidenceType;
  description?: string | null;
  artifact_url?: string | null;
  metadata?: Record<string, any>;
  created_by?: string | null;
  created_at?: string | null;
}

export interface WorkflowCorrectiveActionEvidenceSummary {
  total_count?: number;
  counts_by_type?: Record<string, number>;
  latest_evidence_at?: string | null;
}

export interface WorkflowImpactSummary {
  workflow_id?: string | null;
  status?: 'pending' | 'completed' | 'waived' | string;
  metric_name?: string | null;
  metric_unit?: string | null;
  denominator?: string | null;
  denominator_label?: string | null;
  baseline_window?: {
    start?: string | null;
    end?: string | null;
  };
  post_action_window?: {
    start?: string | null;
    end?: string | null;
  };
  followup_window?: {
    start?: string | null;
    end?: string | null;
  };
  baseline_value?: number | null;
  followup_value?: number | null;
  baseline_exposure_count?: number;
  post_action_exposure_count?: number;
  baseline_exposure_rate?: number;
  post_action_exposure_rate?: number;
  percent_reduction?: number;
  corrective_action_timestamp?: string | null;
  intervention_reference?: string | null;
  effectiveness_claim_status?: string | null;
  measurement_status?: 'sufficient_data' | 'insufficient_data' | 'waived' | string;
  absolute_event_reduction?: number;
  confidence_level?: 'directional' | 'insufficient_data' | string;
  display_status?: string | null;
  display_headline?: string | null;
  display_summary?: string | null;
  roi_classification?:
    | 'positive_measured'
    | 'positive_directional'
    | 'neutral'
    | 'negative'
    | 'insufficient_data'
    | 'waived'
    | 'not_applicable'
    | string;
  recommended_next_step?: string | null;
  estimated_value?: number | null;
  financial_roi_summary?: string | null;
  impact_waived?: boolean;
  waived_by?: string | null;
  waived_at?: string | null;
  waiver_reason?: string | null;
  waiver_scope?: string | null;
  waiver_permission_level?: string | null;
  audit_event_id?: string | null;
  waiver_summary?: string | null;
  measurement_trust?: {
    level?: string | null;
    effectiveness_claim_allowed?: boolean;
    reason?: string | null;
    claim_boundary?: string | null;
    passed_gates?: Array<Record<string, any>>;
    failed_gates?: Array<Record<string, any>>;
    comparability?: Record<string, any>;
    observability?: Record<string, any>;
  } | null;
  impact_measurement?: {
    pivot_timestamp?: string | null;
    pivot_basis?: string | null;
    baseline_window_start?: string | null;
    baseline_window_end?: string | null;
    followup_window_start?: string | null;
    followup_window_end?: string | null;
    baseline_incident_count?: number | null;
    followup_incident_count?: number | null;
    baseline_elapsed_hours?: number | null;
    followup_elapsed_hours?: number | null;
    baseline_monitored_hours?: number | null;
    followup_monitored_hours?: number | null;
    baseline_observation_coverage_percent?: number | null;
    followup_observation_coverage_percent?: number | null;
    denominator_basis?: 'monitored_observation_time' | 'elapsed_wall_clock' | 'not_available' | string | null;
    denominator_trust?: 'trusted' | 'needs_review' | 'insufficient_data' | string | null;
    baseline_rate_per_hour?: number | null;
    followup_rate_per_hour?: number | null;
    percent_reduction?: number | null;
    measurement_status?: 'available' | 'directional' | 'needs_review' | 'insufficient_data' | string | null;
    measurement_notes?: string[];
  } | null;
}

export interface SafetyReportWorkflowAnchor {
  schema_version?: 'safety_report_workflow_anchor.v1' | string | null;
  workflow_id?: string | null;
  group_key?: string | null;
  review_anchor_incident_id?: string | null;
  review_anchor_selection_reason?: string | null;
  proof_scope_id?: string | null;
  selected_report_window?: {
    from?: string | null;
    to?: string | null;
  };
  exact_incident?: boolean | null;
  review_focus_defaults?: {
    evidence_review?: string | null;
    action_owner?: string | null;
    corrective_action?: string | null;
    impact_measurement?: string | null;
  };
  scope?: {
    site_id?: string | null;
    zone_id?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
    incident_type?: string | null;
  };
  source_context?: string | null;
  representative_safety_case_id?: string | null;
}

export interface ReportProjection {
  schema_version: string;
  anchor_incident_id?: string | null;
  proof_scope_id?: string | null;
  safety_cases?: Array<Record<string, any>>;
  report_claims?: ReportClaim[];
  customer_summary?: {
    summary_sentence?: string;
    what_happened_text?: string;
    review_status_text?: string;
    limitation_text?: string;
    recurrence_note?: string;
  };
  technical_trace?: {
    anchor_incident_id?: string | null;
    proof_scope_id?: string | null;
    anchor_pattern_source?: ReportClaim['pattern_source'];
    claim_trace?: Array<Record<string, any>>;
  };
  pattern_trace?: {
    pattern_id?: string | null;
    source_type?: 'incident_group' | string | null;
    recurrence_source?: 'incident_groups' | string | null;
    group_key?: string | null;
    representative_incident_id?: string | null;
    representative_safety_case_id?: string | null;
    pattern_state?: 'isolated' | 'emerging_pattern' | 'recurring_hotspot' | string | null;
    pattern_label?: string | null;
    pattern_summary?: string | null;
    review_priority?: 'low' | 'medium' | 'high' | string | null;
    selected_window_alert_count?: number | null;
    lookback_signal_count?: number | null;
    lookback_active_days?: number | null;
    customer_risk_supported?: boolean;
    risk_disposition?: string | null;
    source_context?: string | null;
    recurrence_note?: string | null;
    semantic_metrics?: CustomerMetricSemantic[] | null;
  };
  risk_patterns?: Array<{
    pattern_id?: string | null;
    source_type?: 'incident_group' | string | null;
    recurrence_source?: 'incident_groups' | string | null;
    group_key?: string | null;
    pattern_state?: 'isolated' | 'emerging_pattern' | 'recurring_hotspot' | string | null;
    pattern_label?: string | null;
    pattern_summary?: string | null;
    review_priority?: 'low' | 'medium' | 'high' | string | null;
    zone_id?: string | null;
    representative_incident_id?: string | null;
    representative_safety_case_id?: string | null;
    selected_window_signal_count?: number | null;
    lookback_signal_count?: number | null;
    lookback_active_days?: number | null;
    source_context?: string | null;
    non_production_mode?: boolean;
    recurrence_note?: string | null;
  }>;
  label_bundle?: ReportProjectionLabelBundle;
  report_counts?: ReportProjectionCounts;
  window_context?: ReportProjectionWindowContext;
  workflow_anchor?: SafetyReportWorkflowAnchor | null;
  evidence_breakdown?: ReportProjectionEvidenceBreakdown;
  workflow_state?: ReportProjectionWorkflowState;
  source_context?: {
    source_context?: string | null;
    non_production_mode?: boolean;
    source_type?: string | null;
    production_claim_allowed?: boolean;
    real_world_outcome_claimed?: boolean;
    risk_reduction_claim_allowed?: boolean;
  };
}

export interface EvidencePackResponse {
  ok: boolean;
  mode?: string;
  async_reason?: string;
  job?: {
    job_id?: string | null;
    status?: string;
    report_kind?: string;
    progress_pct?: number | null;
    eta_seconds?: number | null;
    [key: string]: any;
  };
  error?: string;
  detail?: string;
  missing_components?: string[];
  report_visuals?: ReportVisual[];
  persisted_report?: SafetyReportRecord;
  evidence_pack?: {
    schema_version: string;
    bundle_version: string;
    generated_at: string;
    site_id: string;
    org_id: string;
    time_range: { from: string; to: string };
    contents: Record<string, any>;
    proof_scope?: Record<string, any>;
    signed_hashes: { manifest_sha256?: string; artifacts?: Array<{ kind: string; sha256: string }> };
    retention_policy?: { policy?: string; expires_at?: string };
    model_versions?: Record<string, string>;
    camera_firmware?: Record<string, string>;
    config_hash?: string;
    report_projection?: ReportProjection;
    workflow_anchor?: SafetyReportWorkflowAnchor | null;
  };
  links?: { download_zip?: string };
}

export interface ReliabilityBundlePreviewResponse {
  ok: boolean;
  report_visuals?: ReportVisual[];
  org_id?: string;
  site_id?: string;
  scope?: string;
  scope_id?: string | null;
  operator_front_page?: {
    generated_at?: string;
    summary?: {
      red_count?: number;
      amber_count?: number;
      issue_count?: number;
    };
    items?: Array<{
      key?: string;
      title?: string;
      severity?: string;
      count?: number;
      detail?: string;
      how_to_fix?: string;
      links?: {
        diagnostics?: string;
        incidents?: string;
        device_control?: string;
        live_view?: string;
      };
    }>;
    trust_interpretation?: {
      results_trustworthy?: boolean;
      headline?: string;
      trust_impact?: string;
      signoff_blocker?: string | null;
    };
    top_actions?: Array<{
      title?: string;
      priority?: string;
      detail?: string;
      links?: {
        diagnostics?: string;
        incidents?: string;
        device_control?: string;
        live_view?: string;
      };
    }>;
  };
  metrics?: any;
  rollup?: any;
}

export interface ReliabilityProofPackPreviewResponse {
  ok: boolean;
  report_visuals?: ReportVisual[];
  evidence_pack?: EvidencePackResponse['evidence_pack'];
  persisted_report?: SafetyReportRecord;
  links?: {
    download_zip?: string;
  };
  manifest?: any;
  summary_lines?: string[];
  proof_readiness?: ProofReadinessModel;
  case_file?: {
    summary?: {
      incidents_total?: number;
      acknowledged_total?: number;
      resolved_total?: number;
      open_total?: number;
      high_or_critical_total?: number;
    };
    top_actions?: Array<{
      title?: string;
      detail?: string;
      links?: {
        incident?: string;
        diagnostics?: string;
        device_control?: string;
        live_view?: string;
      };
    }>;
    timeline?: Array<any>;
    incidents?: Array<any>;
    evidence_index?: Array<any>;
  };
}

export type ProofReadinessStatus = 'READY' | 'READY_WITH_WARNINGS' | 'NOT_READY' | 'NEEDS_REVIEW' | string;

export interface ProofReadinessVerdict {
  status: ProofReadinessStatus;
  reason?: string;
  factors?: string[];
}

export interface ProofReadinessSummary {
  high_risk_incidents?: number;
  total_incidents?: number;
  reviewed_high_risk?: number;
  unreviewed_high_risk?: number;
  insufficient_evidence_incidents?: number;
  demo_or_test_activity_incidents?: number;
  evidence_coverage_rate?: number;
  cameras_online?: number | null;
  cameras_total?: number | null;
  top_hotspot?: string | null;
  reliability_status?: string;
  /** Backend: unavailable | healthy | degraded | failing */
  evidence_operator_status?: string;
  reliability_operator_status?: string;
  risk_pattern_summary?: {
    pattern_id?: string | null;
    source_type?: string | null;
    group_key?: string | null;
    recurrence_source?: string | null;
    representative_incident_id?: string | null;
    representative_safety_case_id?: string | null;
    state?: 'isolated' | 'emerging_pattern' | 'recurring_hotspot' | string;
    label?: string;
    top_hotspot?: string | null;
    top_hotspot_incident_count?: number | null;
    concentration_percent?: number | null;
    repeat_pattern_count?: number | null;
    strongest_pattern_label?: string | null;
    strongest_pattern_count?: number | null;
    summary?: string | null;
  } | null;
}

export interface ProofReadinessIssue {
  id?: string;
  title?: string;
  kind?: string;
  severity?: string;
  count?: number;
  why_it_matters?: string;
  recommended_action?: string;
  links?: Record<string, string | undefined>;
}

export interface ProofReadinessEvidence {
  complete_count?: number;
  partial_count?: number;
  failed_count?: number;
  missing_high_risk_clips?: number;
  delayed_capture_count?: number;
  clip_coverage_rate?: number;
  snapshot_coverage_rate?: number;
}

export interface ProofReadinessReliability {
  event_to_evidence_p95_sec?: number | null;
  capture_failure_rate?: number | null;
  processing_failure_rate?: number | null;
  upload_delay_p95_sec?: number | null;
  cameras_online_rate?: number | null;
  devices_online_rate?: number | null;
}

export interface ProofReadinessAction {
  id?: string;
  label?: string;
  priority?: 'high' | 'medium' | 'low' | string;
  reason?: string;
  href?: string | null;
}

export interface ProofReadinessTrust {
  increases?: string[];
  decreases?: string[];
}

export interface ProofReadinessModel {
  verdict?: ProofReadinessVerdict;
  summary?: ProofReadinessSummary;
  top_issues?: ProofReadinessIssue[];
  incidents_curated?: any[];
  evidence?: ProofReadinessEvidence;
  reliability?: ProofReadinessReliability;
  actions?: ProofReadinessAction[];
  trust?: ProofReadinessTrust;
}

export type CraReadinessFormat = 'json' | 'pdf' | 'zip';

export interface CraReadinessRunInput {
  task?: string;
  camera_view_id?: string;
  // Option B: edge models export precomputed frames to cloud (no raw clip upload).
  source?: 'edge_precomputed' | string;
  edge_device_id?: string;
  include_edge_frames_zip?: boolean;
  // Option A: live capture from a registered camera_id (RTSP) on the backend.
  camera_id?: string;
  capture_sec?: number;
  duration_sec?: number;
  clipFile?: File | null;
  precomputedFile?: File | null;
  roiFile?: File | null;
  roiJson?: Record<string, any> | null;
  deviceMetadataFile?: File | null;
  deviceMetadataJsonInline?: Record<string, any> | null;
  acceptance_mode?: boolean;
  captureProtocolFile?: File | null;
  captureProtocolJsonInline?: Record<string, any> | null;
  capture_scenario?: string;
  annotate_frames?: boolean;
  include_raw_frames_json?: boolean;
  analysis_fps?: number;
  max_duration_sec?: number;
  max_frames?: number;
  format?: CraReadinessFormat;
}

export interface CraReadinessJsonResponse {
  ok: boolean;
  error?: string;
  detail?: string;
  camera_view_id?: string;
  task?: string;
  report?: any;
  manifest?: any;
  artifacts?: {
    report_json?: string;
    report_pdf?: string;
    manifest_json?: string;
    frame_archives?: string[];
    frame_summaries?: string[];
  };
  runtime?: {
    elapsed_ms?: number;
  };
}

export type SiteReadinessFormat = 'json' | 'pdf' | 'zip';

export interface SiteReadinessRunInput {
  siteProfileFile?: File | null;
  camerasFile?: File | null;
  site_profile_path?: string;
  cameras_path?: string;
  mode?: 'install' | 'audit' | 'precheck';
  task_profile?: string;
  rtsp_stability_sec?: number;
  rtsp_jitter_fail_ms?: number;
  switch_model?: string;
  poe_total_watts?: number;
  poe_port_class?: string;
  poe_ports?: number;
  powered_cameras?: number;
  cameras_transport?: 'wired' | 'wifi' | 'mixed';
  rf_rssi_db?: number;
  rf_channel?: number;
  rf_packet_loss_pct?: number;
  rf_measurement_method?: string;
  format?: SiteReadinessFormat;
}

export interface SiteReadinessJsonResponse {
  ok: boolean;
  error?: string;
  detail?: string;
  mode?: string;
  report?: any;
  artifacts?: {
    site_readiness_report_json?: string;
    site_readiness_report_pdf?: string | null;
    signoff_summary_pdf?: string | null;
    installer_acceptance_pack_zip?: string;
    installer_acceptance_manifest_json?: string | null;
  };
  runtime?: {
    elapsed_ms?: number;
  };
}

export interface IncidentsRulePolicyDoc {
  schema_version?: number;
  org_id?: string;
  site_id?: string;
  generated_at?: string;
  limits?: {
    top_problem_areas?: number;
    top_problem_types?: number;
    top_actions?: number;
  };
  thresholds?: {
    ppe_red_per_hour_by_zone?: number;
    dwell_amber_trend_multiplier?: number;
    high_critical_red_count?: number;
    reliability_camera_offline_sec?: number;
    reliability_camera_low_fps?: number;
    reliability_inference_uptime_min_pct?: number;
    reliability_backlog_warn_depth?: number;
    reliability_backlog_warn_replay?: number;
  };
  rules?: {
    no_incidents_scope_grey?: boolean;
  };
  classification?: {
    safety_critical_types?: string[];
    operational_types?: string[];
  };
  must_act_incident_types?: string[];
}

export interface IncidentsRulePolicyActiveResponse {
  ok: boolean;
  org_id: string;
  site_id: string;
  version: number;
  active: boolean;
  from_db: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  policy: IncidentsRulePolicyDoc;
}

export interface IncidentsRulePolicyVersionsResponse {
  ok: boolean;
  org_id: string;
  site_id: string;
  active_version: number;
  active_from_db: boolean;
  items: Array<{
    org_id: string;
    site_id: string;
    version: number;
    active: boolean;
    created_by?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  }>;
}

export interface IncidentsRulePolicySaveInput {
  org_id?: string;
  site_id?: string;
  policy: IncidentsRulePolicyDoc;
  activate?: boolean;
  version?: number;
  created_by?: string;
}

export interface IncidentsRulePolicyPreviewInput {
  org_id?: string;
  site_id?: string;
  from_date?: string;
  to_date?: string;
  device_id?: string;
  zone_id?: string;
  camera_ids?: string[];
  policy_ids?: string[];
  policy_type?: string;
  policy: IncidentsRulePolicyDoc;
}

export interface IncidentsRulePolicyPreviewResponse {
  ok: boolean;
  error?: string;
  detail?: string;
  org_id?: string;
  site_id?: string;
  filters?: any;
  dataset_summary?: {
    total_incidents?: number;
    high_or_critical_total?: number;
    dwell_current?: number;
    dwell_previous_window?: number;
  };
  baseline?: {
    version?: number;
    from_db?: boolean;
    evaluation?: any;
  };
  candidate?: {
    evaluation?: any;
  };
  delta?: {
    risk_flags_added?: string[];
    risk_flags_removed?: string[];
    top_actions_count_delta?: number;
    top_problem_areas_limit_delta?: number;
    top_problem_types_limit_delta?: number;
  };
}

export interface AsyncReportJobCreateResponse {
  ok: boolean;
  job?: {
    job_id?: string | null;
    report_kind?: string;
    status?: string;
    cache_hit?: boolean;
    created_at?: string;
  };
  result?: any;
}

export interface AsyncReportJobStatusResponse {
  ok: boolean;
  job?: {
    job_id?: string | null;
    report_kind?: string;
    status?: 'queued' | 'running' | 'completed' | 'failed' | string;
    created_at?: string;
    started_at?: string | null;
    completed_at?: string | null;
    error?: string | null;
    result_status_code?: number | null;
  };
  result?: any;
}

export interface ReportJobListItem {
  job_id?: string | null;
  report_kind?: string;
  status?: 'queued' | 'running' | 'completed' | 'partial' | 'failed' | 'expired' | string;
  terminal_status?: string | null;
  created_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
  progress_pct?: number | null;
  eta_seconds?: number | null;
  request_fingerprint?: string | null;
  artifact_expires_at?: string | null;
  error?: string | null;
}

export interface ReportJobListResponse {
  ok: boolean;
  items?: ReportJobListItem[];
  jobs?: ReportJobListItem[];
  limit?: number;
  offset?: number;
  total?: number;
  has_more?: boolean;
}

export interface ReportSreHealthResponse {
  ok: boolean;
  as_of?: string;
  cache?: {
    entries?: number;
    ttl_sec?: number;
  };
  jobs?: {
    counts?: Record<string, number>;
    retention_sec?: number;
  };
  latency?: Record<string, { count?: number; p50_ms?: number | null; p95_ms?: number | null; max_ms?: number | null }>;
  slo_targets?: {
    report_generation_p95_ms?: number;
    api_p95_ms?: number;
    job_queue_backlog_warn?: number;
  };
}

export type PersistedSafetyReportStatus = 'draft' | 'finalized' | 'archived' | 'superseded' | string;

export interface PersistedSafetyReportWorkflowAnchor {
  workflow_id?: string | null;
  representative_incident_id?: string | null;
  scope?: {
    site_id?: string | null;
    zones?: string[];
    risk_type?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
  };
  time_window?: {
    start?: string | null;
    end?: string | null;
    from?: string | null;
    to?: string | null;
  };
  anchor_created_at?: string | null;
  anchor_version?: number | null;
}

export interface SafetyReportRecord {
  report_id?: string | null;
  org_id?: string | null;
  site_id?: string | null;
  workflow_id?: string | null;
  finding_id?: string | null;
  status?: PersistedSafetyReportStatus;
  version?: number;
  workflow_anchor?: PersistedSafetyReportWorkflowAnchor | null;
  report_payload?: Record<string, any>;
  finalized_payload?: Record<string, any> | null;
  report_content?: Record<string, any>;
  report_content_source?: 'report_payload' | 'finalized_payload' | string | null;
  created_from_queue_state_id?: string | null;
  derived_from_report_id?: string | null;
  finalized_at?: string | null;
  archived_at?: string | null;
  superseded_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  window_start_utc?: string | null;
  window_end_utc?: string | null;
  finalization_blocked?: boolean | null;
  finalization_blockers?: Array<{ code?: string | null; message?: string | null }> | null;
}

export interface SafetyReportDraftMutationResponse {
  ok: boolean;
  report?: SafetyReportRecord;
  created_new?: boolean;
  resumed_existing?: boolean;
  old_anchor?: PersistedSafetyReportWorkflowAnchor;
  new_anchor?: PersistedSafetyReportWorkflowAnchor;
  blocked?: boolean;
  blockers?: Array<{ code: string; message: string }>;
  workflow_summary?: Record<string, any>;
  error?: string;
}

export interface WorkflowCorrectiveActionListResponse {
  ok: boolean;
  workflow_id?: string | null;
  active_actions?: WorkflowCorrectiveAction[];
  completed_actions?: WorkflowCorrectiveAction[];
  cancelled_actions?: WorkflowCorrectiveAction[];
  evidence_summary?: WorkflowCorrectiveActionEvidenceSummary | null;
  error?: string;
}

export interface WorkflowCorrectiveActionMutationResponse {
  ok: boolean;
  action?: WorkflowCorrectiveAction;
  evidence?: WorkflowCorrectiveActionEvidence;
  error?: string;
}

export type ValidationTaskStatus = 'open' | 'in_progress' | 'completed' | 'waived' | 'cancelled' | string;
export type ClaimReadinessBlockerStatus = 'open' | 'resolved' | 'waived' | string;
export type EffectivenessValidationWorkflowStatus = 'open' | 'in_progress' | 'completed' | 'cancelled' | string;
export type ClaimReadinessControlStatus =
  | 'resolved'
  | 'needs_attestation'
  | 'needs_confirmation'
  | 'needs_evidence'
  | 'blocked'
  | 'not_applicable'
  | string;
export type ClaimReadinessResolutionType =
  | 'auto_clear'
  | 'attestation_required'
  | 'evidence_required'
  | 'field_confirmation_required'
  | 'manual_review_required'
  | 'not_applicable'
  | string;

export interface ClaimReadinessBlocker {
  blocker_id?: string | null;
  validation_workflow_id?: string | null;
  report_id?: string | null;
  blocker_type?: string | null;
  source_label?: string | null;
  details?: string | null;
  status?: ClaimReadinessBlockerStatus;
  resolved_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ValidationTask {
  task_id?: string | null;
  validation_workflow_id?: string | null;
  blocker_id?: string | null;
  blocker_type?: string | null;
  source_label?: string | null;
  title?: string | null;
  description?: string | null;
  assignee?: string | null;
  status?: ValidationTaskStatus;
  evidence_note?: string | null;
  waiver_reason?: string | null;
  control_payload?: Record<string, any>;
  result_payload?: Record<string, any>;
  due_date?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface EffectivenessValidationWorkflow {
  validation_workflow_id?: string | null;
  workflow_id?: string | null;
  report_id?: string | null;
  safety_case_id?: string | null;
  status?: EffectivenessValidationWorkflowStatus;
  title?: string | null;
  created_by?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  blockers?: ClaimReadinessBlocker[];
  tasks?: ValidationTask[];
  progress?: {
    total_tasks?: number;
    open_tasks?: number;
    completed_tasks?: number;
    waived_tasks?: number;
  } | null;
}

export interface ClaimReadinessNextAction {
  label?: string | null;
  owner_area?: string | null;
  route_hint?: string | null;
}

export interface ClaimBoundarySummary {
  outcome_claimed?: boolean;
  exposure_reduction_claimed?: boolean;
  demo_source?: boolean;
  wording?: string | null;
}

export interface ClaimReadinessTaskControlState {
  status?: ValidationTaskStatus;
  source_label?: string | null;
  task_id?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
  evidence_note?: string | null;
  waiver_reason?: string | null;
  control_payload?: Record<string, any>;
  result_payload?: Record<string, any>;
  resolved?: boolean;
}

export interface ClaimReadinessControlSummary {
  control_id?: string | null;
  label?: string | null;
  status?: ClaimReadinessControlStatus;
  resolution_type?: ClaimReadinessResolutionType;
  owner_area?: string | null;
  evidence_refs?: string[];
  resolved_at?: string | null;
  resolved_by?: string | null;
  source?: string | null;
  attestation_required?: boolean;
  auto_resolution_reason?: string | null;
  blocking?: boolean;
  next_action?: ClaimReadinessNextAction | null;
  task_id?: string | null;
  source_label?: string | null;
}

export interface WorkflowClaimReadinessResponse {
  ok: boolean;
  workflow_id?: string | null;
  finding_id?: string | null;
  report_id?: string | null;
  overall_status?: 'ready' | 'blocked' | string;
  resolved_count?: number;
  total_count?: number;
  controls?: ClaimReadinessControlSummary[];
  blockers?: ClaimReadinessControlSummary[];
  next_action?: ClaimReadinessNextAction | null;
  claim_boundary?: ClaimBoundarySummary | null;
  controls_by_type?: Record<string, ClaimReadinessTaskControlState>;
  validation_workflows?: EffectivenessValidationWorkflow[];
  error?: string;
}

export interface ValidationWorkflowMutationResponse {
  ok: boolean;
  validation_workflow?: EffectivenessValidationWorkflow | null;
  reused_existing_workflow?: boolean;
  new_blockers?: number;
  error?: string;
  message?: string;
}

export interface ValidationTaskMutationResponse {
  ok: boolean;
  task?: ValidationTask;
  previous_task?: ValidationTask;
  validation_workflow?: EffectivenessValidationWorkflow | null;
  error?: string;
  message?: string;
}

export interface SafetyReportOpenResponse {
  ok: boolean;
  report?: SafetyReportRecord;
  report_id?: string | null;
  workflow_id?: string | null;
  opened_workflow?: boolean;
  opened_representative_incident?: boolean;
  replacement_representative_incident_id?: string | null;
  workflow_summary?: {
    workflow_id?: string | null;
    corrective_actions?: {
      active_actions?: WorkflowCorrectiveAction[];
      completed_actions?: WorkflowCorrectiveAction[];
      cancelled_actions?: WorkflowCorrectiveAction[];
      evidence_summary?: WorkflowCorrectiveActionEvidenceSummary | null;
    };
    impact_summary?: WorkflowImpactSummary | null;
  } | null;
  notice?: string | null;
  error?: string;
}

export interface ObservationDispositionQueueItem {
  incident_id?: string | null;
  event_ts?: string | null;
  incident_type?: string | null;
  zone_id?: string | null;
  camera_id?: string | null;
  device_id?: string | null;
  status?: string | null;
  review_outcome?: string | null;
  version?: number | null;
}

export type OperatorLifecycleNextAction = 'verify_observations' | 'establish_initial_baseline' | 'record_control' | 'none';

export interface OperatorLifecycleResponse {
  ok?: boolean;
  error?: string;
  report_id?: string | null;
  safety_case_id?: string | null;
  workflow_id?: string | null;
  operational_state?: string | null;
  review?: {
    status?: string | null;
    reviewed?: number;
    pending?: number;
  } | null;
  control?: {
    status?: string | null;
    current_applicability?: string | null;
  } | null;
  signoff?: {
    status?: string | null;
    claim_readiness_resolved?: number;
    claim_readiness_total?: number;
    outcome_withheld?: boolean;
    approval_required?: boolean;
  } | null;
  next_operator_action?: OperatorLifecycleNextAction | string | null;
  measurement_references?: Record<string, unknown> | null;
}

export interface ObservationDispositionQueueResponse {
  ok?: boolean;
  error?: string;
  detail?: string;
  report_id?: string | null;
  report_status?: string | null;
  safety_case_id?: string | null;
  workflow_id?: string | null;
  window?: { start?: string | null; end?: string | null } | null;
  location_label?: string | null;
  scope?: {
    zone_id?: string | null;
    camera_id?: string | null;
    device_id?: string | null;
  } | null;
  progress?: {
    candidate_episode_count?: number | null;
    pending_review_count?: number | null;
    reviewed_count?: number | null;
    confirmed_count?: number | null;
    not_relevant_count?: number | null;
    unreviewable_count?: number | null;
    authorized_count?: number | null;
    false_positive_count?: number | null;
    duplicate_count?: number | null;
    needs_more_review_count?: number | null;
  } | null;
  items?: ObservationDispositionQueueItem[];
  next_incident_id?: string | null;
}

export type ScanVerificationState =
  | 'NO_SCAN'
  | 'NOT_STARTED'
  | 'FREEZE_NOT_READY'
  | 'QUEUE_UNAVAILABLE'
  | 'FROZEN_EMPTY'
  | 'FROZEN'
  | 'LOAD_FAILED'
  | 'STALE_REVISION'
  | 'FINALIZED_IMMUTABLE'
  | 'IDEMPOTENCY_CONFLICT';

export type ScanFormalDisposition = 'UNREVIEWED' | 'CONFIRM' | 'CLEAR' | 'UNRESOLVED';

export interface ScanVerificationProgress {
  total: number;
  unreviewed: number;
  confirmed: number;
  cleared: number;
  unresolved: number;
  complete: boolean;
}

export interface ScanVerificationMember {
  member_id?: string | null;
  ordinal?: number | null;
  event_ts?: string | null;
  incident_type?: string | null;
  zone_id?: string | null;
  camera_id?: string | null;
  device_id?: string | null;
  source_provenance?: Record<string, unknown> | null;
  disposition?: ScanFormalDisposition | string | null;
  reason_code?: string | null;
  reviewer?: {
    user_id?: string | null;
    display?: string | null;
    email?: string | null;
  } | null;
  decided_at?: string | null;
  revision?: number | null;
  disposition_id?: string | null;
  proposal?: {
    disposition?: ScanFormalDisposition | string | null;
    reason?: string | null;
    reason_code?: string | null;
    source?: string | null;
    original_outcome?: string | null;
    original_actor?: string | null;
    original_decided_at?: string | null;
    actionable?: boolean;
  } | null;
  adopted_from?: {
    source?: string | null;
    original_outcome?: string | null;
    reason?: string | null;
    original_actor?: string | null;
    original_decided_at?: string | null;
  } | null;
}

export interface ScanVerificationResponse {
  ok?: boolean;
  state?: ScanVerificationState | string | null;
  error?: string;
  detail?: string;
  report_id?: string | null;
  report_status?: string | null;
  version?: number | null;
  safety_case_id?: string | null;
  workflow_id?: string | null;
  verification_set_id?: string | null;
  writable?: boolean;
  freeze_ready?: boolean;
  reason?: string | null;
  window?: { start?: string | null; end?: string | null } | null;
  scope?: {
    zone_id?: string | null;
    camera_id?: string | null;
    device_id?: string | null;
    risk_type?: string | null;
  } | null;
  location_label?: string | null;
  freeze?: {
    frozen_at?: string | null;
    frozen_by?: string | null;
    population_digest?: string | null;
    frozen_member_count?: number | null;
    membership_rule?: string | null;
    membership_rule_version?: string | null;
    status?: string | null;
  } | null;
  progress?: ScanVerificationProgress | null;
  proposals?: {
    remaining?: number;
    present?: boolean;
  } | null;
  members?: ScanVerificationMember[];
  late_arrivals?: {
    count?: number;
    present?: boolean;
    items?: Array<{
      incident_id?: string | null;
      event_ts?: string | null;
      noticed_at?: string | null;
    }>;
  } | null;
  preview?: {
    eligible_count?: number;
    truncated?: boolean;
  } | null;
  current?: ScanVerificationMember & {
    from_disposition?: string | null;
    idempotent_replay?: boolean;
  };
}

export interface ScanMeasurementReconciliation {
  ok?: boolean;
  state?: string | null;
  report_id?: string | null;
  verification?: {
    total?: number;
    confirmed?: number;
    cleared?: number;
    unresolved?: number;
    unreviewed?: number;
    complete?: boolean;
    empty_population?: boolean;
    population_digest?: string | null;
  };
  measurement?: {
    numerator?: { value?: number; semantic?: string; identity?: string | null };
    denominator?: {
      usable_seconds?: number | null;
      usable_hours?: number | null;
      proven?: boolean;
      reason?: string | null;
    };
    rate?: {
      value?: number | null;
      label?: string;
      issuable?: boolean;
      withheld_reasons?: string[];
    };
    gates?: {
      review_complete?: boolean;
      coverage_sufficient?: boolean;
      rate_issuable?: boolean;
    };
  };
  late_arrivals?: {
    count?: number;
    present?: boolean;
    claim_withheld_reason?: string | null;
  };
  unresolved_disclosed?: boolean;
}

export interface ScanVerificationDispositionRequest {
  disposition: 'CONFIRM' | 'CLEAR' | 'UNRESOLVED';
  reason_code?: string | null;
  expected_revision: number;
  idempotency_key: string;
}

export interface ScanVerificationAdoptRequest {
  expected_revision: number;
  idempotency_key: string;
}

export interface MeasurementAssuranceCheck {
  key: string;
  ok: boolean;
  detail: string;
}

export interface MeasurementAssuranceReadiness {
  gate?: string;
  current_status?: string | null;
  preview_status_if_admitted?: string | null;
  met_ready?: boolean;
  checks?: MeasurementAssuranceCheck[];
  missing_count?: number;
  next_actions?: string[];
  suggested_supplements?: Record<string, unknown>;
  evaluation_run_id?: string | null;
  thresholds?: Record<string, number>;
}

export interface MeasurementAssuranceResponse {
  ok: boolean;
  report_id?: string | null;
  org_id?: string | null;
  site_id?: string | null;
  scope?: {
    device_id?: string | null;
    camera_id?: string | null;
    policy_type?: string | null;
    zone_id?: string | null;
  } | null;
  current_measurement?: Record<string, unknown> | null;
  evaluation_run?: {
    id?: string | null;
    policy_type?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
    labeled_count?: number | null;
    sample_count?: number | null;
    metrics_json?: Record<string, unknown> | null;
    created_at?: string | null;
  } | null;
  recent_evaluation_runs?: Array<{
    id?: string | null;
    labeled_count?: number | null;
    precision?: number | null;
    created_at?: string | null;
  }>;
  sample_counts?: {
    labeled?: number;
    unlabeled?: number;
    total?: number;
    source?: string | null;
  } | null;
  admitted_sample_counts?: {
    labeled?: number | null;
    unlabeled?: number | null;
    total?: number | null;
    source?: string | null;
  } | null;
  readiness?: MeasurementAssuranceReadiness | null;
  error?: string;
  detail?: string;
  allows_signoff?: boolean;
  signoff_reason?: string | null;
  field_verification_gate?: {
    allowed?: boolean;
    code?: string | null;
    item_keys?: string[];
    customer_label?: string | null;
  } | null;
}

export interface AdmitReportEvaluationResponse {
  ok: boolean;
  measurement?: Record<string, unknown> | null;
  evaluation_run_id?: string | null;
  payload_column?: string | null;
  report?: SafetyReportRecord | null;
  error?: string;
  detail?: string;
}

export interface DecisionReadinessRecommendedActionItem {
  label?: string | null;
  owner?: string | null;
  due?: string | null;
  type?: 'interim_control' | 'verification' | 'engineering_review' | 'training' | 'measurement' | 'other' | string | null;
}

export interface DecisionReadinessPayload {
  what_changed?: {
    status?: 'available' | 'not_available' | string | null;
    summary?: string | null;
    baseline_rate_per_hour?: number | null;
    followup_rate_per_hour?: number | null;
    change_percent?: number | null;
    comparison_basis?: 'normalized_hourly_rate' | 'raw_counts' | 'not_available' | string | null;
    claim_strength?: 'measured' | 'directional' | 'insufficient' | string | null;
    comparison_note?: string | null;
  } | null;
  risk_driver?: {
    status?: 'available' | 'requires_review' | 'not_available' | string | null;
    summary?: string | null;
    event_category?: string | null;
    safety_rule?: string | null;
    trigger?: string | null;
  } | null;
  concentration?: {
    status?: 'available' | 'not_available' | string | null;
    top_zone?: string | null;
    top_time_period?: string | null;
    top_event_category?: string | null;
    top_camera?: string | null;
    summary?: string | null;
  } | null;
  severe_exposure?: {
    status?: 'identified' | 'requires_review' | 'not_available' | string | null;
    severity?: 'serious_injury_or_fatality_potential' | 'high' | 'medium' | 'low' | 'not_classified' | string | null;
    summary?: string | null;
    rationale?: string | null;
  } | null;
  control_weakness?: {
    status?: 'identified' | 'requires_review' | 'not_available' | string | null;
    summary?: string | null;
    suspected_control?: string | null;
    control_category?: 'elimination' | 'substitution' | 'engineering' | 'administrative' | 'ppe' | 'unknown' | string | null;
    rationale?: string | null;
  } | null;
  corrective_action?: {
    status?: 'completed' | 'open' | 'not_started' | 'not_available' | string | null;
    exists?: boolean | null;
    summary?: string | null;
    owner?: string | null;
    owner_role?: string | null;
    due_date?: string | null;
    completed_at?: string | null;
    action_type?: string | null;
    notes?: string | null;
    verification_method?: string | null;
    evidence_of_completion?: string | null;
    missing_fields?: string[];
  } | null;
  control_strength_review?: {
    status?: 'strong' | 'moderate' | 'weak' | 'requires_review' | 'not_available' | string | null;
    summary?: string | null;
    current_control_level?: 'elimination' | 'substitution' | 'engineering' | 'administrative' | 'ppe' | 'unknown' | string | null;
    recommended_higher_control?: string | null;
    rationale?: string | null;
  } | null;
  recommended_next_action?: {
    priority?: 'high' | 'medium' | 'low' | 'not_available' | string | null;
    summary?: string | null;
    actions?: DecisionReadinessRecommendedActionItem[];
  } | null;
  effectiveness_verification?: {
    status?: 'verified' | 'directional' | 'requires_followup' | 'not_available' | string | null;
    summary?: string | null;
    method?: string | null;
    baseline_window?: string | null;
    followup_window?: string | null;
    matched_window_required?: boolean | null;
    claim_boundary?: string | null;
  } | null;
  decision_quality?: {
    level?: 'ready_for_action' | 'needs_review' | 'insufficient_data' | string | null;
    summary?: string | null;
    missing_inputs?: string[];
  } | null;
  warnings?: string[];
}

export type WorksheetStatus = 'answered' | 'partially_answered' | 'not_available' | 'requires_more_data';

export interface EhsWorksheetItem {
  id?: string;
  question?: string;
  status?: WorksheetStatus | string | null;
  answer?: string | null;
  evidence_source?: string[];
  missing_inputs?: string[];
  recommended_followup?: string | null;
}

export interface EhsReviewWorksheetPayload {
  completeness_score_percent?: number | null;
  status_counts?: Partial<Record<WorksheetStatus, number>>;
  overall_status?: 'decision_ready' | 'partially_decision_ready' | 'not_decision_ready' | string | null;
  summary?: string | null;
  questions?: EhsWorksheetItem[];
}

export interface SafetyScanBuyerReportPayload {
  executive_summary?: {
    safety_finding?: {
      risk_detected?: string | null;
      event_signals?: number | null;
      primary_hotspot?: string | null;
      peak_period?: string | null;
      why_it_matters?: string | null;
    } | null;
    recommended_decision?: {
      decision?: string | null;
      immediate_action?: string | null;
      follow_up_action?: string | null;
      decision_readiness?: string | null;
      safety_action_status?: string | null;
      impact_validation_status?: string | null;
      workflow_status_summary?: string | null;
    } | null;
    executive_narrative?: string | null;
    claim_boundary?: {
      supported_claims?: string[];
      not_supported_claims?: string[];
      summary?: string | null;
    } | null;
  } | null;
  confidence_posture?: {
    finding_confidence?: string | null;
    impact_confidence?: string | null;
    improvement_claim_status?: string | null;
    decision_readiness?: string | null;
    safety_action_status?: string | null;
    impact_validation_status?: string | null;
    workflow_status_summary?: string | null;
    finding_actionable?: boolean;
    summary?: string | null;
  } | null;
  export_context?: {
    site_area?: string | null;
    scan_window?: string | null;
    observed_signals?: number | null;
    reviewed_signal_records?: number | null;
    representative_snapshots?: number | null;
    representative_clips?: number | null;
  } | null;
  impact_measurement_interpretation?: {
    baseline_count?: number | null;
    followup_count?: number | null;
    directional_change?: string | null;
    directional_change_label?: string | null;
    directional_change_summary?: string | null;
    measurement_confidence?: string | null;
    formal_claim_status?: string | null;
    claim_withheld_reason?: string | null;
    baseline_rate_per_hour?: number | null;
    followup_rate_per_hour?: number | null;
    formal_rate_claim_allowed?: boolean;
    impact_status?: string | null;
  } | null;
  buyer_decision?: {
    decision?: string | null;
    rationale?: string | null;
    immediate_action?: string | null;
    follow_up_action?: string | null;
    evidence_basis?: string[];
    claim_boundary?: string | null;
  } | null;
  corrective_action_controls?: {
    current_control_level?: string | null;
    recommended_controls?: Array<{
      level?: string | null;
      label?: string | null;
      examples?: string[];
      rationale?: string | null;
    }>;
    administrative_note?: string | null;
  } | null;
  follow_up_scan_plan?: {
    recommended_window?: string | null;
    recommended_window_display?: string | null;
    baseline_window_display?: string | null;
    followup_window_display?: string | null;
    same_zones?: boolean;
    zones?: string[];
    corrective_action_verified?: string | null;
    minimum_observation_hours?: number | null;
    success_metric?: string | null;
    claim_conditions?: string[];
  } | null;
  exposure_priority?: {
    frequency?: string | null;
    severity?: string | null;
    recurrence?: string | null;
    evidence_confidence?: string | null;
    overall?: string | null;
    formula_note?: string | null;
  } | null;
  claim_boundary_summary?: {
    items?: Array<{
      claim?: string | null;
      status?: string | null;
      note?: string | null;
    }>;
    summary?: string | null;
  } | null;
  evidence_gallery_notes?: {
    summary?: string | null;
    representative_vs_full?: string | null;
  } | null;
  comparative_context?: {
    top_drivers?: ComparativeSafetyIntelligencePayload['top_drivers'];
    trend_stability?: ComparativeSafetyIntelligencePayload['trend_stability'];
  } | null;
  investigation_alignment?: InvestigationAlignmentPayload | null;
  policy_report_context?: PolicyReportContextPayload | null;
  safety_control_review?: SafetyControlReviewPayload | null;
  safety_control_scan?: {
    safety_control_review?: SafetyControlReviewPayload | null;
  } | null;
  claim_language_policy?: ClaimLanguagePolicyPayload | null;
  window_labels?: WindowLabelPolicyPayload | null;
  customer_ready_sections?: Record<string, unknown> | null;
  customer_layout_mode?: string | null;
}

export interface InvestigationAlignmentPayload {
  occurrence_families?: string[];
  cause_patterns?: string[];
  contributing_factor_themes?: string[];
  ux_summary?: string | null;
  report_summary?: string | null;
  claim_boundary?: string | null;
  source_label?: string | null;
}

export interface ClaimsRoiAlignmentPayload {
  claims_alignment_category?: string | null;
  claims_cost_driver_family?: string | null;
  commercial_priority?: string | null;
  target_industry_hints?: string[];
  roi_rationale?: string | null;
  ux_summary?: string | null;
  report_summary?: string | null;
  claim_boundary?: string | null;
  source_label?: string | null;
  industry_sector_hints?: string[];
  classification_unit_hints?: string[];
  recommended_pilot_templates?: string[];
  recommended_first_scan?: string | null;
  buyer_pain_language?: string | null;
  sales_discovery_questions?: string[];
  benefit_type_relevance?: string[];
  claim_characteristic_hints?: string[];
  commercial_priority_basis?: string[];
}

export interface SafetyControlReviewPayload {
  control_objective?: string | null;
  observed_exposure?: string | null;
  existing_controls_to_verify?: string[];
  potential_control_gaps?: string[];
  field_verification_questions?: string[];
  verification_status?: string | null;
  effectiveness_boundary?: string | null;
  closure_requirements?: string[];
  claim_boundary?: string | null;
  ux_summary?: string | null;
  report_summary?: string | null;
  source_label?: string | null;
  primary_control_lens?: string | null;
  control_level?: string | null;
  potential_control_gap?: string | null;
  field_verification_question?: string | null;
  closure_requirement?: string | null;
  follow_up_observation?: string | null;
}

export interface PolicyReportContextPayload {
  policy_type?: string | null;
  policy_alias?: string | null;
  pack_key?: string | null;
  scan_template?: string | null;
  exposure_category?: string | null;
  risk_family?: string | null;
  hazard_exposure_label?: string | null;
  short_exposure_label?: string | null;
  control_review_focus?: string[];
  control_review_prompts?: string[];
  evidence_supports_review?: string | null;
  claim_boundary_profile?: string | null;
  scan_template_label?: string | null;
  exposure_category_label?: string | null;
  report_title_suffix?: string | null;
  investigation_alignment?: InvestigationAlignmentPayload | null;
  claims_roi_alignment?: ClaimsRoiAlignmentPayload | null;
  safety_control_review?: SafetyControlReviewPayload | null;
}

export interface ClaimLanguagePolicyPayload {
  allow_improvement_language?: boolean;
  allow_percent_reduction?: boolean;
  customer_message?: string | null;
  window_labels?: Record<string, string | null>;
  diagnostic_only?: boolean;
  allow_effectiveness_language?: boolean;
  allow_roi_language?: boolean;
  allow_accident_prevention_language?: boolean;
  impact_claim_status?: string | null;
  corrective_action_comparison_mode?: string | null;
  customer_summary?: string | null;
}

export interface SafetyScanDocumentControlPayload {
  document_title?: string | null;
  document_subtitle?: string | null;
  revision?: string | null;
  revision_status?: string | null;
  status_badge?: string | null;
  prepared_by?: string | null;
  prepared_for?: string | null;
  confidentiality?: string | null;
  report_timestamp?: string | null;
  package_timestamp?: string | null;
  report_id?: string | null;
  scan_window?: string | null;
  claim_boundary_note?: string | null;
  executive_statement?: string | null;
  scope_note?: string | null;
}

export interface CorrectiveActionComparisonPayload {
  schema_version?: 'corrective_action_comparison.v1' | string | null;
  comparison_mode?:
    | 'corrective_action_comparison'
    | 'diagnostic_comparison'
    | 'no_comparison_available'
    | string
    | null;
  comparison_status?: 'claimable' | 'not_claimable' | 'unavailable' | string | null;
  customer_section_title?: string | null;
  customer_summary?: string | null;
  corrective_action?: {
    exists?: boolean;
    status?: string | null;
    owner?: string | null;
    action_type?: string | null;
    summary?: string | null;
    completed_at?: string | null;
  } | null;
  claim_policy?: {
    count_comparison_allowed?: boolean;
    rate_comparison_allowed?: boolean;
    effectiveness_claim_allowed?: boolean;
    improvement_language_allowed?: boolean;
    percent_reduction_allowed?: boolean;
  } | null;
  customer_warnings?: string[];
}

export interface WindowLabelPolicyPayload {
  windows_timeline_valid?: boolean;
  diagnostic_only?: boolean;
  first_window_label?: string | null;
  second_window_label?: string | null;
  post_action_column_label?: string | null;
  first_window_detail?: string | null;
  second_window_detail?: string | null;
  comparison_descriptor?: string | null;
  effectiveness_comparison_note?: string | null;
  improvement_claim_note?: string | null;
  misalignment_reasons?: string[];
}

export interface SafetyEvidenceCardPayload {
  schema_version?: 'safety_evidence_card.v1' | string | null;
  evidence_id?: string | null;
  incident_id?: string | null;
  timestamp?: string | null;
  site?: string | null;
  area?: string | null;
  camera_id?: string | null;
  risk_type?: string | null;
  safety_rule?: string | null;
  snapshot_path?: string | null;
  snapshot_uri?: string | null;
  clip_path?: string | null;
  zone_polygon?: number[][] | null;
  detections?: Array<Record<string, unknown>> | null;
  person_bbox?: number[] | null;
  review_status?: string | null;
  risk_level?: string | null;
  recommended_action?: string | null;
  media_uri?: string | null;
  source_status?: string | null;
  workflow_id?: string | null;
  site_id?: string | null;
  zone_id?: string | null;
  is_anchor?: boolean | null;
  claims_visual_evidence?: boolean | null;
  evidence_quality_status?: string | null;
  card_render_blocked?: boolean | null;
  visual_alignment_failure_codes?: string[] | null;
  rendered_image_data_url?: string | null;
  rendered_image_base64?: string | null;
  rendered_image_mime_type?: string | null;
  primary_image_label?: string | null;
  secondary_evidence_label?: string | null;
  secondary_snapshot_uri?: string | null;
  secondary_clip_uri?: string | null;
  lighting?: {
    condition?: string | null;
    label?: string | null;
    confidence?: string | null;
    sample_type?: string | null;
    is_calibrated_lux?: boolean | null;
    source?: string | null;
  } | null;
  bbox_status_label?: string | null;
  incident_subject_label?: string | null;
  trigger_person_verification_label?: string | null;
  trigger_person_verification_status?: string | null;
  caption?: string | null;
  kind?: string | null;
  revision_bound_media?: 'unavailable' | string | null;
  media_note?: string | null;
  type?: string | null;
}

export interface SafetyScanActionPlanRowPayload {
  risk_driver?: string | null;
  finding?: string | null;
  recommended_control?: string | null;
  action?: string | null;
  control_type?: string | null;
  owner?: string | null;
  priority?: string | null;
  due_date?: string | null;
  verification_method?: string | null;
  verification_evidence?: string | null;
  follow_up_gate?: string | null;
  status?: string | null;
}

export interface SafetyScanActionPlanPayload {
  status?: string | null;
  rows?: SafetyScanActionPlanRowPayload[];
  sign_off?: {
    ehs_reviewer?: string | null;
    site_manager?: string | null;
  } | null;
  notes?: string | null;
}

export interface SafetyScanReportV2Payload {
  schema_version?: 'safety_scan_report.v2' | string | null;
  report_id?: string | null;
  executive_summary?: SafetyScanBuyerReportPayload['executive_summary'];
  evidence_cards?: SafetyEvidenceCardPayload[];
  action_plan?: SafetyScanActionPlanPayload | null;
  claim_boundary?: {
    summary?: string | null;
    supported_claims?: string[];
    not_supported_claims?: string[];
    items?: Array<{
      claim?: string | null;
      status?: string | null;
      note?: string | null;
    }>;
  } | null;
  claim_language_policy?: ClaimLanguagePolicyPayload | null;
  corrective_action_comparison?: CorrectiveActionComparisonPayload | null;
  investigation_alignment?: InvestigationAlignmentPayload | null;
  customer_ready_sections?: Record<string, unknown> | null;
  document_control?: SafetyScanDocumentControlPayload | null;
  window_labels?: WindowLabelPolicyPayload | null;
  customer_layout_mode?: string | null;
  report_mode?: string | Record<string, unknown> | null;
  section_keys?: string[];
  reviewer_decision_status?: string | null;
  privacy_redacted?: boolean | null;
  field_verification_status?: string | null;
  customer_owned_risk_boundary?: string | null;
  corrective_action_status?: string | null;
  follow_up_claim_boundary?: string | null;
  closure_status?: string | null;
  secure_evidence_reference_policy?: string | null;
  finalized_report_snapshot?: SafetyReportFinalizedSnapshot | null;
  live_workflow_followup?: SafetyReportLiveWorkflowFollowup | null;
  appendix_refs?: Array<{
    id?: string | null;
    label?: string | null;
    layer?: number | null;
  }>;
}

export interface ComparativeSafetyIntelligencePayload {
  scope?: {
    org_id?: string | null;
    site_id?: string | null;
    report_id?: string | null;
    comparison_window_start?: string | null;
    comparison_window_end?: string | null;
    dimensions_available?: string[];
  } | null;
  top_drivers?: Array<{
    rank?: number;
    driver_type?: string | null;
    label?: string | null;
    count?: number | null;
    rate_per_monitored_hour?: number | null;
    dimension_share_percent?: number | null;
    severity?: string | null;
    decision_note?: string | null;
  }>;
  best_worst_comparisons?: Array<{
    dimension?: string | null;
    best_label?: string | null;
    best_rate_per_monitored_hour?: number | null;
    worst_label?: string | null;
    worst_rate_per_monitored_hour?: number | null;
    comparison_basis?: string | null;
    denominator_trust?: string | null;
    summary?: string | null;
    missing_inputs?: string[];
  }>;
  trend_stability?: {
    status?: string | null;
    summary?: string | null;
    time_points_available?: number | null;
    method?: string | null;
    warnings?: string[];
  } | null;
  process_shift_detection?: {
    status?: string | null;
    summary?: string | null;
    evidence?: string[];
    missing_inputs?: string[];
  } | null;
  transfer_learning_opportunities?: Array<{
    source_label?: string | null;
    target_label?: string | null;
    hypothesis?: string | null;
    required_validation?: string | null;
  }>;
  comparative_confidence?: {
    level?: string | null;
    summary?: string | null;
    missing_inputs?: string[];
  } | null;
}

export interface SafetyReportLiveWorkflowFollowup {
  workflow_id?: string | null;
  representative_incident_id?: string | null;
  zone_id?: string | null;
  camera_id?: string | null;
  device_id?: string | null;
  review_status?: string | null;
  review_status_label?: string | null;
  workflow_status?: string | null;
  workflow_status_label?: string | null;
  has_new_activity_since_report?: boolean;
  newer_representative_incident_id?: string | null;
  active_draft_report_id?: string | null;
  latest_finalized_report_id?: string | null;
  recommended_next_step?: string | null;
  recommended_next_step_label?: string | null;
  latest_review_status_label?: string | null;
  latest_activity_at?: string | null;
  updated_at?: string | null;
  source_workflow_updated_at?: string | null;
  workflow_changed_since_signoff?: boolean;
  recent_changes_label?: string | null;
}

export interface SafetyReportFinalizedSnapshot {
  report_id?: string | null;
  status?: PersistedSafetyReportStatus | null;
  status_label?: string | null;
  finalized_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  workflow_id?: string | null;
  finding_id?: string | null;
  representative_incident_id?: string | null;
  summary?: string | null;
  summary_details?: string[];
  scope?: {
    site_id?: string | null;
    zones?: string[];
    risk_type?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
  } | null;
  time_window?: {
    start?: string | null;
    end?: string | null;
  } | null;
  display_labels?: Record<string, string | null> | null;
  claim_boundary?: string | null;
  claims_summary?: Record<string, unknown> | null;
  signoff_state?: {
    status?: PersistedSafetyReportStatus | null;
    status_label?: string | null;
    finalized_at?: string | null;
  } | null;
  evidence_included?: {
    representative_incident_id?: string | null;
    representative_evidence?: Record<string, unknown> | null;
  } | null;
  workflow_summary?: Record<string, unknown> | null;
  customer_summary?: Record<string, unknown> | null;
}

export interface SafetyReportArtifactDetailResponse {
  ok: boolean;
  persisted_report?: SafetyReportRecord | null;
  artifact?: {
    report_id?: string | null;
    status?: PersistedSafetyReportStatus | null;
    status_label?: string | null;
    finalized_at?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    workflow_id?: string | null;
    finding_id?: string | null;
    representative_incident_id?: string | null;
    summary?: string | null;
    summary_details?: string[];
    scope?: {
      site_id?: string | null;
      zones?: string[];
      risk_type?: string | null;
      device_id?: string | null;
      camera_id?: string | null;
    } | null;
    time_window?: {
      start?: string | null;
      end?: string | null;
    } | null;
    display_labels?: {
      finding_headline?: string | null;
      risk_level?: string | null;
      risk_badge_label?: string | null;
      risk_description?: string | null;
      trigger_label?: string | null;
      detection_at?: string | null;
      site_label?: string | null;
      area_label?: string | null;
      activity_type_label?: string | null;
      event_basis_label?: string | null;
      scope_reference_label?: string | null;
    } | null;
    claims_summary?: {
      validated_claim_text?: string | null;
      key_insight_text?: string | null;
      claim_confidence_label?: string | null;
      evidence_sufficiency_label?: string | null;
      data_normalization_label?: string | null;
      comparison_method_label?: string | null;
      exposure_basis_text?: string | null;
      callout_text?: string | null;
      windows_comparable?: boolean;
    } | null;
  } | null;
  report_projection?: ReportProjection | null;
  report_visuals?: ReportVisual[];
  evidence_gallery?: {
    items?: Array<{
      id?: string | null;
      incident_id?: string | null;
      kind?: 'snapshot' | 'clip' | string | null;
      title?: string | null;
      captured_at?: string | null;
      thumbnail_uri?: string | null;
      media_uri?: string | null;
      status?: string | null;
      area_label?: string | null;
      rule_label?: string | null;
      is_anchor?: boolean;
    }>;
    total_items?: number;
    anchor_incident_id?: string | null;
  } | null;
  impact_measurement_graph?: {
    default_granularity?: 'hour' | 'day' | 'week' | string | null;
    granularities?: Array<'hour' | 'day' | 'week' | string>;
    summary?: {
      percent_reduction?: number | null;
      baseline_exposure_rate_per_day?: number | null;
      post_action_exposure_rate_per_day?: number | null;
      baseline_exposure_count?: number | null;
      post_action_exposure_count?: number | null;
      baseline_elapsed_hours?: number | null;
      followup_elapsed_hours?: number | null;
      baseline_monitored_hours?: number | null;
      followup_monitored_hours?: number | null;
      baseline_observation_coverage_percent?: number | null;
      followup_observation_coverage_percent?: number | null;
      denominator_basis?: 'monitored_observation_time' | 'elapsed_wall_clock' | 'not_available' | string | null;
      denominator_trust?: 'trusted' | 'needs_review' | 'insufficient_data' | string | null;
      measurement_status?: 'available' | 'directional' | 'needs_review' | 'insufficient_data' | string | null;
      measurement_notes?: string[];
    } | null;
    series?: Record<
      string,
      Array<{
        id?: string | null;
        label?: string | null;
        granularity?: string | null;
        unit_label?: string | null;
        value?: number | null;
      }>
    >;
    comparability?: {
      windows_comparable?: boolean;
      windows_same_duration?: boolean;
      measurement_note?: string | null;
      denominator_basis?: 'monitored_observation_time' | 'elapsed_wall_clock' | 'not_available' | string | null;
      denominator_trust?: 'trusted' | 'needs_review' | 'insufficient_data' | string | null;
    } | null;
  } | null;
  technical_details?: {
    customer_summary?: {
      summary_sentence?: string;
      what_happened_text?: string;
      review_status_text?: string;
      corrective_action_status_text?: string;
      impact_status_text?: string;
      measurement_trust_level?: string;
      measurement_trust_text?: string;
      effectiveness_claim_allowed?: boolean;
      roi_status_text?: string;
      claim_boundary_text?: string;
    } | null;
    impact_measurement_graph?: SafetyReportArtifactDetailResponse['impact_measurement_graph'];
    evidence_gallery?: SafetyReportArtifactDetailResponse['evidence_gallery'];
    review_status?: {
      report_status?: string | null;
      finalization_status?: string | null;
      finalization_blockers?: Array<{ code?: string | null; message?: string | null }>;
    } | null;
    report_claims?: Array<{
      claim?: string;
      status?: string;
      notes?: string;
    }>;
    recommendation_snapshot?: SafetyCaseRecommendationSnapshot | null;
    decision_readiness?: DecisionReadinessPayload | null;
    source_context?: ReportProjection['source_context'];
    report_context?: PolicyReportContextPayload | null;
    policy_report_context?: PolicyReportContextPayload | null;
    safety_control_review?: SafetyControlReviewPayload | null;
  } | null;
  export_manifest?: {
    package_type?: string | null;
    report_status?: string | null;
    finalization_status?: string | null;
    files?: Array<{ path?: string | null; role?: string | null }>;
    package_display?: {
      preparation_status_label?: string | null;
      report_status_label?: string | null;
      package_type_label?: string | null;
      file_count?: number | null;
    } | null;
  } | null;
  customer_report_markdown?: string | null;
  recommendation_snapshot?: SafetyCaseRecommendationSnapshot | null;
  decision_readiness?: DecisionReadinessPayload | null;
  comparative_safety_intelligence?: ComparativeSafetyIntelligencePayload | null;
  ehs_review_worksheet?: EhsReviewWorksheetPayload | null;
  safety_scan_buyer_report?: SafetyScanBuyerReportPayload | null;
  safety_scan_report_v2?: SafetyScanReportV2Payload | null;
  finalized_report_snapshot?: SafetyReportFinalizedSnapshot | null;
  live_workflow_followup?: SafetyReportLiveWorkflowFollowup | null;
  /** @deprecated Use live_workflow_followup */
  live_followup?: SafetyReportLiveWorkflowFollowup | null;
  error?: string;
}

export type SignedRecordJsonValue =
  | string
  | number
  | boolean
  | null
  | SignedRecordJsonValue[]
  | { [key: string]: SignedRecordJsonValue };

export type SignedRecordJsonObject = { [key: string]: SignedRecordJsonValue };

export interface CanonicalSignedRecordBody {
  identity: {
    record_id: string;
    revision: number;
    lifecycle_state: 'signed' | 'superseded';
    title: string;
    condition_type: string | null;
    condition_summary: string | null;
    provenance: string;
    provenance_label: string;
  };
  governance: {
    signer_label: string;
    signer_state: string;
    signer_name: string | null;
    signer_role: string | null;
    signer_provenance: string | null;
    signed_at: string | null;
    prepared_by: string | null;
    prepared_for: string | null;
    confidentiality: string | null;
  };
  scope: {
    organization: SignedRecordJsonValue;
    site: SignedRecordJsonValue;
    camera: SignedRecordJsonValue;
    zone: SignedRecordJsonValue;
    policy: SignedRecordJsonValue;
    observation_window_label: string | null;
  };
  finding: {
    summary: SignedRecordJsonValue;
    reviewed_episodes: number | null;
    usable_hours: number | null;
    rate_per_usable_hour: number | null;
    coverage_percent: number | null;
    peak_location: SignedRecordJsonValue;
    peak_window: SignedRecordJsonValue;
    peak_counts: SignedRecordJsonValue;
    time_bands: SignedRecordJsonValue[];
    /** Non-claim operational numbers when claimable rates are unavailable. */
    operational_observation?: {
      label?: string | null;
      confirmed_episodes?: number | null;
      candidate_episodes?: number | null;
      monitored_hours?: number | null;
      elapsed_hours?: number | null;
      denominator_trust?: string | null;
      measurement_status?: string | null;
    } | null;
  };
  observation_funnel?: {
    detector_record_count?: number | null;
    candidate_episode_count?: number | null;
    confirmed_count?: number | null;
    reviewed_count?: number | null;
    false_positive_count?: number | null;
    duplicate_count?: number | null;
    needs_more_review_count?: number | null;
    pending_count?: number | null;
    authorized_count?: number | null;
    not_relevant_count?: number | null;
    unreviewable_count?: number | null;
  };
  control: {
    description: string | null;
    owner: string | null;
    status: 'proposed' | 'selected' | 'implemented' | 'verified';
    implemented_at: string | null;
    verification_method: string | null;
    location: string | null;
    field_verifier: string | null;
    verified_at: string | null;
    implementation_evidence_reference: string | null;
    supporting_control: string | null;
    notes: string | null;
    actions: SignedRecordJsonValue[];
  };
  recheck: {
    status: 'not_planned' | 'planned' | 'running' | 'not_comparable' | 'inconclusive' | 'claimable';
    summary: string | null;
    reviewed_episodes: number | null;
    usable_hours: number | null;
    rate_per_usable_hour: number | null;
    coverage_percent: number | null;
    window: string | null;
    start: SignedRecordJsonValue;
    end: SignedRecordJsonValue;
    comparable: boolean;
    measurement_denominator_verified: boolean;
    comparability_checklist: Array<{ label: string; met: boolean }>;
  };
  result: {
    label: 'pending' | 'decreased' | 'no_change' | 'increased' | 'inconclusive' | 'not_comparable';
    headline: string | null;
    explanation: string | null;
    absolute_rate_change: number | null;
    descriptive_percent_change: number | null;
    comparability_met: boolean;
    supports: SignedRecordJsonValue[];
    does_not_establish: SignedRecordJsonValue[];
  };
  next_decision: SignedRecordJsonValue;
  evidence: Array<{
    evidence_id: string;
    title: string | null;
    metadata: SignedRecordJsonObject;
    snapshot_ref: string | null;
    clip_ref: string | null;
    illustrative: boolean;
  }>;
  technical_annex: {
    available: boolean;
    source_revision_matches: boolean;
    measurement_assurance: SignedRecordJsonValue;
    convergence: SignedRecordJsonValue;
    coverage: SignedRecordJsonValue;
    scan_review?: SignedRecordJsonValue;
    walkdown?: SignedRecordJsonValue;
    provenance?: SignedRecordJsonValue;
  };
}

export interface SignedSafetyControlRecordDetail {
  executive_summary: SignedRecordJsonObject | null;
  document_control: SignedRecordJsonObject | null;
  condition_scope: {
    condition: SignedRecordJsonObject | null;
    scope: SignedRecordJsonObject;
    observation_window: SignedRecordJsonObject | null;
  };
  baseline: {
    detail: SignedRecordJsonObject | null;
    window: SignedRecordJsonObject | null;
    metrics: SignedRecordJsonObject;
  };
  site_selected_control: {
    detail: SignedRecordJsonObject | null;
    comparison: SignedRecordJsonObject | null;
    action_plan: SignedRecordJsonObject | null;
  };
  recheck: {
    detail: SignedRecordJsonObject | null;
    window: SignedRecordJsonObject | null;
    metrics: SignedRecordJsonObject;
    comparability: SignedRecordJsonObject | null;
  };
  impact_measurement: {
    graph: SignedRecordJsonObject | null;
    visuals: SignedRecordJsonValue[];
  };
  scan_report?: SignedRecordJsonObject | null;
  evidence: {
    gallery: SignedRecordJsonObject | null;
    cards: SignedRecordJsonValue[];
    total_items: number;
    availability_reason?: 'available' | 'viewer_not_authorized'
      | 'representative_cards_not_retained' | 'no_evidence_retained' | string;
  };
  limitations_claim_boundaries: {
    limitations: SignedRecordJsonValue;
    claims: SignedRecordJsonValue;
    claim_boundary: SignedRecordJsonValue;
    claim_language_policy: SignedRecordJsonObject | null;
  };
}

export interface SignedSafetyControlRecord {
  record_id: string;
  revision: number;
  lifecycle: 'signed' | 'superseded';
  signed_at?: string | null;
  signer: { label: string; state: string };
  record_provenance: { label: string; state: string };
  identity?: {
    condition_label?: string | null;
    summary?: string | null;
  } | null;
  scope?: {
    site_id?: string | null;
    zone_id?: string | null;
    risk_type?: string | null;
    camera_id?: string | null;
    device_id?: string | null;
  } | null;
  related?: { safety_case_id?: string | null; workflow_id?: string | null };
  history?: {
    current_revision: number;
    revisions: Array<{
      revision: number;
      parent_revision?: number | null;
      signature_state?: string | null;
      current: boolean;
    }>;
  };
  live_followup?: SafetyReportLiveWorkflowFollowup | null;
  new_activity?: SafetyReportLiveWorkflowFollowup | null;
  baseline?: { rate?: number | null; window?: Record<string, unknown> | null } | null;
  site_selected_control?: {
    summary?: string | null;
    status?: string | null;
    owner?: string | null;
    notes?: string | null;
    completed_at?: string | null;
  } | null;
  recheck?: { rate?: number | null; window?: Record<string, unknown> | null } | null;
  recheck_result: {
    label: string;
    state?: string | null;
    completeness: string;
    comparable: boolean;
    detail?: string | null;
    percent_reduction?: number | null;
  };
  signed_body: CanonicalSignedRecordBody;
  detail: SignedSafetyControlRecordDetail;
  exports?: {
    pdf_available?: boolean;
    pdf_mode?: 'customer' | 'external_safe' | null;
  };
}

export interface SignedSafetyControlRecordEvidenceResponse {
  ok: boolean;
  report_id?: string;
  revision?: number;
  evidence?: {
    cards: SafetyEvidenceCardPayload[];
    representative_count: number;
    total_items: number;
    availability_reason?: 'available' | 'viewer_not_authorized'
      | 'representative_cards_not_retained' | 'no_evidence_retained' | string;
  };
  error?: string;
}

export interface WorkflowReportCandidate {
  workflow_id?: string | null;
  risk_type?: string | null;
  site_id?: string | null;
  zone_id?: string | null;
  camera_id?: string | null;
  device_id?: string | null;
  representative_incident_id?: string | null;
  grouped_incident_count?: number;
  review_status?: {
    status?: string | null;
    status_label?: string | null;
    review_complete?: boolean;
    review_outcome?: string | null;
  } | null;
  corrective_action_status?: {
    status?: string | null;
    status_label?: string | null;
  } | null;
  impact_measurement_status?: string | null;
  impact_summary?: WorkflowImpactSummary | null;
  workflow_review_status?: {
    status?: string | null;
    status_label?: string | null;
  } | null;
  active_draft_report_id?: string | null;
  latest_finalized_report_id?: string | null;
  report_readiness?: string | null;
  recommended_action?: string | null;
  updated_at?: string | null;
}

export interface WorkflowReportCandidatesResponse {
  ok: boolean;
  candidates?: WorkflowReportCandidate[];
  error?: string;
}

export interface SafetyDashboardSummary {
  candidate_workflows?: number;
  saved_findings?: number;
  active_drafts?: number;
  blocked_reports?: number;
  ready_for_signoff?: number;
  finalized_reports?: number;
  packages_available?: number;
  impact_pending?: number;
  worsening_after_action?: number;
}

export interface SafetyDashboardRowActions {
  open_report_detail?: boolean;
  open_review?: boolean;
  save_finding?: boolean;
  create_or_resume_draft?: boolean;
  view_finalized_report?: boolean;
  download_package?: boolean;
  finalize_report?: boolean;
}

export interface SafetyDashboardScanRevisionItem {
  revision?: number | null;
  parent_revision?: number | null;
  signature_state?: string | null;
  sha256_pdf?: string | null;
  artifact_links?: Record<string, unknown> | null;
}

export interface SafetyDashboardScanHistoryItem {
  report_id?: string | null;
  scan_id?: string | null;
  derived_from_report_id?: string | null;
  predecessor_report_id?: string | null;
  report_family_id?: string | null;
  scan_name?: string | null;
  status?: string | null;
  scope_start?: string | null;
  scope_end?: string | null;
  signed_at?: string | null;
  /** MANUAL | SCHEDULED | FOLLOW_UP when known from persisted scan metadata. */
  trigger_type?: string | null;
  package_available?: boolean;
  has_new_activity_after_this_scan?: boolean;
  download_available?: boolean;
  current_revision?: number | string | null;
  signed_by?: string | null;
  signature_state?: string | null;
  durable_report_id?: string | null;
  revisions?: SafetyDashboardScanRevisionItem[] | null;
  addenda?: Array<Record<string, unknown>> | null;
  /** Per-Scan operational population. Not case-level observation_count. */
  measured_observation_count?: number | null;
  reviewed_observation_count?: number | null;
  confirmed_observation_count?: number | null;
  usable_hours?: number | null;
  coverage_state?: string | null;
  verification_state?: string | null;
  /** Exact condition IDs persisted in this report revision's frozen measurement basis. */
  frozen_condition_ids?: string[] | null;
}

export interface SafetyFindingRecord {
  finding_id?: string | null;
  safety_case_id?: string | null;
  workflow_id?: string | null;
  followup_owner?: string | null;
  followup_team?: string | null;
  has_followup_owner?: boolean;
  followup_owner_explicit?: boolean;
  followup_owner_assigned_at?: string | null;
  followup_owner_assigned_by?: string | null;
  followup_owner_cleared_at?: string | null;
  followup_owner_cleared_by?: string | null;
  followup_owner_source?: string | null;
  org_id?: string | null;
  site_id?: string | null;
  status?: string | null;
  case_admission_status?: 'admitted' | 'technical_anchor' | string | null;
  is_managed_case?: boolean;
  safety_case_admitted?: boolean;
  claim_status?: string | null;
  safety_case_status?: string | null;
  safety_case_status_label?: string | null;
  lifecycle_state?: string | null;
  hazard_category?: string | null;
  finding_type?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  source_pattern_id?: string | null;
  source_workflow_updated_at?: string | null;
  measurement_status?: string | null;
  measurement_confidence?: string | null;
  scope?: Record<string, any> | null;
  time_window?: Record<string, any> | null;
  workflow_anchor?: Record<string, any> | null;
  risk_type?: string | null;
  risk_label?: string | null;
  risk_pattern_label?: string | null;
  camera_id?: string | null;
  camera_label?: string | null;
  zone_id?: string | null;
  zone_label?: string | null;
  area_label?: string | null;
  device_id?: string | null;
  device_label?: string | null;
  report_finalized_at?: string | null;
  latest_scan_signed_at?: string | null;
  latest_observation_at?: string | null;
  has_new_activity_since_report?: boolean;
  followup_required?: boolean;
  workflow_changed_since_signoff?: boolean;
  representative_incident_id?: string | null;
  representative_safety_case_id?: string | null;
  followup_measurement?: Record<string, any> | null;
  review_status?: {
    review_complete?: boolean | null;
    review_outcome?: string | null;
    reviewed_at?: string | null;
    reviewed_by?: string | null;
    status?: string | null;
    status_label?: string | null;
  } | null;
  corrective_response_status?: string | null;
  status_snapshot?: Record<string, any> | null;
  recommendations?: SafetyCaseRecommendation[];
  site_preferences_summary?: {
    count?: number;
    top_constraints?: Array<{ constraint_type?: string | null; count?: number | null }>;
    highlights?: string[];
    latest_updated_at?: string | null;
  } | null;
  outcome_learning_summary?: {
    count?: number;
    validated_count?: number;
    directional_count?: number;
    rejected_count?: number;
    repeated_constraints?: Array<{ constraint_type?: string | null; count?: number | null }>;
    highlights?: string[];
    latest_updated_at?: string | null;
  } | null;
  linked_report_count?: number;
  active_draft_report_id?: string | null;
  previous_report_id?: string | null;
  latest_finalized_report_id?: string | null;
  saved_at?: string | null;
  saved_by?: string | null;
}

export interface SafetyFindingMutationResponse {
  ok?: boolean;
  finding?: SafetyFindingRecord | null;
  created_new?: boolean;
  already_saved?: boolean;
  error?: string;
}

export interface SafetyCaseRecord extends SafetyFindingRecord {
  resource_kind?: 'safety_case' | string;
  is_demo_or_looped_source?: boolean;
}

export interface SafetyCaseListResponse {
  ok: boolean;
  safety_cases?: SafetyCaseRecord[];
  items?: SafetyCaseRecord[];
  count?: number;
  scope?: { org_id?: string | null; site_id?: string | null } | null;
  resource_kind?: string;
  error?: string;
}

export interface SafetyCaseOpenResponse {
  ok: boolean;
  safety_case?: SafetyCaseRecord | null;
  finding?: SafetyCaseRecord | null;
  workflow_id?: string | null;
  representative_incident_id?: string | null;
  workflow_summary?: Record<string, any> | null;
  resource_kind?: string;
  error?: string;
}

export interface ControlEvaluationRecord {
  evaluation_id?: string;
  case_id?: string;
  recorded_at?: string | null;
  recorded_by?: string | null;
  control?: Record<string, any> | null;
  before?: Record<string, any> | null;
  after?: Record<string, any> | null;
  descriptive_result?: string | null;
  customer_result?: string | null;
  customer_result_label?: string | null;
  customer_copy?: string | null;
  comparability_verdict?: string | null;
  comparability_reason_codes?: string[];
  limitations?: string[];
  claim_percentage_allowed?: boolean;
  percent_change?: number | null;
  verification_assessment_id?: string | null;
}

export interface CaseTimelineEvent {
  event_id: string;
  event_type: string;
  occurred_at: string;
  case_id?: string;
  title: string;
  summary?: string;
  kind?: string;
  source?: {
    type?: string;
    id?: string;
    revision?: string | number | null;
  };
  related?: Record<string, unknown>;
}

export interface CaseTimelineResponse {
  ok?: boolean;
  safety_case_id?: string;
  case_id?: string;
  events?: CaseTimelineEvent[];
  omissions?: Array<Record<string, unknown>>;
  producer?: string;
  horizon_picker?: boolean;
  mutated?: boolean;
  error?: string;
}

export interface ControlEvaluationsResponse {
  ok?: boolean;
  safety_case_id?: string;
  control_evaluations?: ControlEvaluationRecord[];
  control_evaluation?: ControlEvaluationRecord | null;
  missing?: boolean;
  missing_reason_codes?: string[];
  missing_reasons?: string[];
  horizon_picker?: boolean;
  replayed?: boolean;
  error?: string;
}

export interface NewActivityDecisionResponse {
  ok: boolean;
  decision_kind?: string;
  safety_case_id?: string;
  lifecycle_state?: string | null;
  lifecycle_version?: number | null;
  acknowledged_through?: string | null;
  representative_incident_id?: string | null;
  incident_status?: string | null;
  incident_mutated?: boolean;
  frozen_signed_scan_id?: string | null;
  idempotent_replay?: boolean;
  error?: string;
  detail?: string;
}

export type SafetyCaseRecommendationDecisionState =
  | 'accepted'
  | 'modified'
  | 'rejected'
  | 'deferred'
  | 'needs_internal_review'
  | string;

export interface SafetyCaseRecommendationDecision {
  decision_id?: string | null;
  recommendation_id?: string | null;
  safety_case_id?: string | null;
  finding_id?: string | null;
  workflow_id?: string | null;
  decision_type?: string | null;
  decision_state?: SafetyCaseRecommendationDecisionState | null;
  decision_state_label?: string | null;
  decision_reason?: string | null;
  decision_notes?: string | null;
  corrective_action_id?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
  review_owner?: string | null;
  review_due_at?: string | null;
  decision_context?: Record<string, any>;
  created_at?: string | null;
}

export interface SafetyCaseRecommendationCorrection {
  correction_id?: string | null;
  recommendation_id?: string | null;
  safety_case_id?: string | null;
  finding_id?: string | null;
  workflow_id?: string | null;
  original_text?: string | null;
  corrected_text?: string | null;
  correction_reason?: string | null;
  constraint_type?: string | null;
  corrected_by?: string | null;
  corrected_at?: string | null;
  created_at?: string | null;
}

export interface SafetyCaseRecommendationCorrectiveActionSummary {
  action_id?: string | null;
  recommendation_id?: string | null;
  safety_case_id?: string | null;
  finding_id?: string | null;
  workflow_id?: string | null;
  action_type?: string | null;
  title?: string | null;
  description?: string | null;
  priority?: string | null;
  assignee?: string | null;
  status?: string | null;
  due_date?: string | null;
  completed_at?: string | null;
  validated_at?: string | null;
  implemented_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SafetyCaseRecommendationAuditEvent {
  event_type?: string | null;
  event_label?: string | null;
  at?: string | null;
  actor?: string | null;
  summary?: string | null;
  corrective_action_id?: string | null;
}

export interface SafetyCaseRecommendation {
  recommendation_id?: string | null;
  safety_case_id?: string | null;
  finding_id?: string | null;
  workflow_id?: string | null;
  recommendation_type?: string | null;
  priority?: string | null;
  control_level?: string | null;
  reason?: string | null;
  rationale?: string | null;
  recommendation_text?: string | null;
  original_recommendation_text?: string | null;
  current_recommendation_text?: string | null;
  effective_recommendation_text?: string | null;
  customer_modified_recommendation_text?: string | null;
  generated_by?: string | null;
  generated_at?: string | null;
  status?: string | null;
  generation_context?: Record<string, any>;
  latest_decision?: SafetyCaseRecommendationDecision | null;
  latest_correction?: SafetyCaseRecommendationCorrection | null;
  decision_history?: SafetyCaseRecommendationDecision[];
  correction_history?: SafetyCaseRecommendationCorrection[];
  decisions?: SafetyCaseRecommendationDecision[];
  corrections?: SafetyCaseRecommendationCorrection[];
  linked_corrective_action?: SafetyCaseRecommendationCorrectiveActionSummary | null;
  linked_corrective_actions?: SafetyCaseRecommendationCorrectiveActionSummary[];
  current_decision_state?: SafetyCaseRecommendationDecisionState | null;
  current_decision_state_label?: string | null;
  audit_history?: SafetyCaseRecommendationAuditEvent[];
  can_create_corrective_action?: boolean;
  can_link_corrective_action?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SafetyCaseRecommendationListResponse {
  ok: boolean;
  safety_case_id?: string | null;
  recommendations?: SafetyCaseRecommendation[];
  count?: number;
  error?: string;
}

export interface SafetyCaseRecommendationMutationResponse {
  ok: boolean;
  safety_case_id?: string | null;
  workflow_id?: string | null;
  recommendation?: SafetyCaseRecommendation | null;
  recommendations?: SafetyCaseRecommendation[];
  decision?: SafetyCaseRecommendationDecision | null;
  correction?: SafetyCaseRecommendationCorrection | null;
  corrective_action?: SafetyCaseRecommendationCorrectiveActionSummary | null;
  finding?: SafetyCaseRecord | null;
  error?: string;
}

export interface SafetyCaseRecommendationSnapshot {
  schema_version?: string | null;
  safety_case_id?: string | null;
  report_id?: string | null;
  frozen_at?: string | null;
  summary?: {
    total_recommendations?: number | null;
    accepted_or_modified?: number | null;
    linked_corrective_actions?: number | null;
    rejected?: number | null;
    deferred?: number | null;
    needs_internal_review?: number | null;
  } | null;
  recommendations?: SafetyCaseRecommendation[];
}

export interface SafetyCaseReportOpenResponse {
  ok: boolean;
  report?: SafetyReportRecord | null;
  safety_case?: SafetyCaseRecord | null;
  finding?: SafetyCaseRecord | null;
  report_id?: string | null;
  workflow_id?: string | null;
  linked_report_count?: number;
  linked_report_selection?: string | null;
  resource_kind?: string;
  error?: string;
}

export interface SafetyDashboardRow {
  report_type?: 'pattern' | 'category' | string;
  workflow_id?: string | null;
  finding_id?: string | null;
  safety_case_id?: string | null;
  report_id?: string | null;
  risk_type?: string | null;
  risk_label?: string | null;
  risk_pattern_label?: string | null;
  unsafe_condition_title?: string | null;
  activity_type?: string | null;
  policy_report_context?: PolicyReportContextPayload | null;
  scan_template?: string | null;
  scan_template_label?: string | null;
  exposure_category?: string | null;
  exposure_category_label?: string | null;
  risk_family?: string | null;
  risk_family_label?: string | null;
  hazard_exposure_label?: string | null;
  control_review_focus?: string[] | null;
  claim_boundary_profile?: string | null;
  claim_boundary_profile_label?: string | null;
  safety_case_title?: string | null;
  safety_case_status?: string | null;
  safety_case_status_label?: string | null;
  lifecycle_state?: string | null;
  hazard_category?: string | null;
  owner?: string | null;
  owner_name?: string | null;
  followup_owner?: string | null;
  followup_team?: string | null;
  has_followup_owner?: boolean;
  followup_owner_explicit?: boolean;
  followup_owner_source?: string | null;
  latest_scan_id?: string | null;
  latest_scan_name?: string | null;
  latest_scan_status?: string | null;
  latest_scan_signed_at?: string | null;
  latest_scan_scope_start?: string | null;
  latest_scan_scope_end?: string | null;
  latest_observation_at?: string | null;
  new_activity_count?: number | null;
  semantic_metrics?: CustomerMetricSemantic[] | null;
  review_state?: string | null;
  safety_scan_title?: string | null;
  site_id?: string | null;
  site_label?: string | null;
  area_label?: string | null;
  zone_label?: string | null;
  zone_id?: string | null;
  camera_label?: string | null;
  camera_id?: string | null;
  device_label?: string | null;
  device_id?: string | null;
  representative_incident_id?: string | null;
  workflow_status?: string | null;
  workflow_status_label?: string | null;
  review_status?: string | null;
  review_status_label?: string | null;
  review?: WorkflowReportCandidate['review_status'];
  corrective_action_status?: string | null;
  corrective_action_status_label?: string | null;
  impact_measurement_status?: string | null;
  impact_status_label?: string | null;
  impact_summary?: WorkflowImpactSummary | null;
  claim_readiness?: Partial<WorkflowClaimReadinessResponse> | null;
  measurement_trust_level?: string | null;
  measurement_trust?: Record<string, any> | null;
  effectiveness_claim_allowed?: boolean;
  finding_status?: string | null;
  finding_saved?: boolean;
  case_admission_status?: 'admitted' | 'technical_anchor' | string | null;
  is_managed_case?: boolean;
  safety_case_admitted?: boolean;
  case_state?: string | null;
  case_next_step?: string | null;
  current_scan?: SafetyDashboardScanHistoryItem | null;
  latest_signed_scan?: SafetyDashboardScanHistoryItem | null;
  control_state?: string | null;
  has_new_activity?: boolean;
  attention_reason?: string | null;
  attention_priority?: string | null;
  signoff_status?: string | null;
  finding_saved_at?: string | null;
  finding_saved_by?: string | null;
  report_status?: string | null;
  /** Epic 278 Slice D — server-resolved customer-facing workspace states. */
  customer_scan_workflow_state?: string | null;
  customer_scan_result?: string | null;
  customer_record_state?: string | null;
  customer_control_stage?: string | null;
  customer_risk_pattern_work_state?: string | null;
  customer_attention_work_state?: string | null;
  customer_status_label?: string | null;
  /** Admin-facing integrity warnings for impossible field combos; never customer badges. */
  integrity_warnings?: string[] | null;
  report_artifact_status?: string | null;
  report_created_at?: string | null;
  report_finalized_at?: string | null;
  report_scope_start?: string | null;
  report_scope_end?: string | null;
  finalization_status?: string | null;
  blockers?: Array<{ code?: string | null; message?: string | null }>;
  active_draft_report_id?: string | null;
  active_draft_trigger_type?: string | null;
  latest_finalized_report_id?: string | null;
  latest_scan_trigger_type?: string | null;
  latest_scan_signed_by?: string | null;
  latest_scan_current_revision?: number | string | null;
  package_available?: boolean;
  package_status?: string | null;
  first_detected_at?: string | null;
  observation_count?: number | null;
  representative_evidence_count?: number | null;
  has_new_activity_since_report?: boolean;
  acknowledged_through?: string | null;
  latest_incident_id?: string | null;
  latest_review_status?: string | null;
  latest_review_status_label?: string | null;
  followup_required?: boolean;
  followup_blockers?: Array<{ code?: string | null; message?: string | null }>;
  exposure_change_percent?: number | null;
  exposure_change_direction?: 'improved' | 'worse' | 'unchanged' | string | null;
  financial_roi_status?: string | null;
  recommended_next_step?: string | null;
  status_computed_at?: string | null;
  source_workflow_updated_at?: string | null;
  is_status_stale?: boolean;
  status_source?: 'computed' | 'cached' | 'stale_cached' | string | null;
  workflow_changed_since_signoff?: boolean;
  scan_history?: SafetyDashboardScanHistoryItem[] | null;
  updated_at?: string | null;
  actions?: SafetyDashboardRowActions | null;
}

export interface SafetyDashboardResponse {
  ok: boolean;
  site_id?: string | null;
  summary?: SafetyDashboardSummary;
  workflows?: SafetyDashboardRow[];
  limit?: number;
  error?: string;
}

export interface SafetyPortfolioSiteRef {
  site_id?: string | null;
  display_name?: string | null;
  region_label?: string | null;
  site_owner?: string | null;
}

export interface SafetyPortfolioAttentionItem {
  item_id?: string | null;
  workflow_id?: string | null;
  site?: SafetyPortfolioSiteRef | null;
  item_type?: string | null;
  work_state?: string | null;
  status_label?: string | null;
  updated_at?: string | null;
  owner?: string | null;
  owner_identity?: string | null;
  next_action?: string | null;
  finding_id?: string | null;
  safety_case_id?: string | null;
  report_id?: string | null;
  coverage_context?: {
    scan_workflow_state?: string | null;
    scan_result?: string | null;
    impact_measurement_status?: string | null;
    measurement_trust_level?: string | null;
    integrity_warnings?: string[] | null;
  } | null;
}

export interface SafetyPortfolioSiteCoverage {
  site_id?: string | null;
  display_name?: string | null;
  timezone?: string | null;
  region_label?: string | null;
  site_owner?: string | null;
  included?: boolean;
  data_state?: 'loaded' | 'permission_denied' | 'unavailable' | string;
  workflow_count?: number;
  attention_count?: number;
  data_freshness_at?: string | null;
  reason?: string | null;
  detail?: string | null;
}

export interface SafetyPortfolioResponse {
  ok: boolean;
  org_id?: string | null;
  scope?: {
    scope_type?: string | null;
    org_id?: string | null;
    requested_site_ids?: string[];
    authorized_site_ids?: string[];
    denied_site_ids?: string[];
    partial?: boolean;
    partial_reasons?: string[];
  } | null;
  summary?: {
    operational_counts?: SafetyDashboardSummary;
    sites_requested?: number;
    sites_included?: number;
    sites_denied?: number;
    sites_unavailable?: number;
    data_freshness_at?: string | null;
  } | null;
  attention?: SafetyPortfolioAttentionItem[];
  site_coverage?: SafetyPortfolioSiteCoverage[];
  permissions?: {
    partial?: boolean;
    denied_sites?: Array<{ site_id?: string | null; reason?: string | null }>;
  } | null;
  interpretation?: {
    opaque_aggregate_fields?: string | null;
    counts_require_context?: string[];
  } | null;
  limit?: number;
  error?: string;
}

export const reportsApi = {
  async createAsyncReportJob(input: {
    report_kind: 'incidents_rollup' | 'reliability_proof_pack' | 'reliability_bundle' | 'evidence_pack' | 'cra_readiness';
    params: Record<string, any>;
    use_cache?: boolean;
  }): Promise<AsyncReportJobCreateResponse> {
    const payload = {
      report_kind: input.report_kind,
      params: input.params || {},
      use_cache: input.use_cache !== false,
    };
    const res = await api.post('/api/v1/reports/jobs', payload);
    return res.data as AsyncReportJobCreateResponse;
  },

  async getAsyncReportJob(jobId: string, opts?: { include_result?: boolean }): Promise<AsyncReportJobStatusResponse> {
    const q = new URLSearchParams();
    if (opts?.include_result) q.set('include_result', '1');
    const qs = q.toString();
    const res = await api.get(`/api/v1/reports/jobs/${encodeURIComponent(jobId)}${qs ? `?${qs}` : ''}`);
    return res.data as AsyncReportJobStatusResponse;
  },

  async listReportJobs(params?: {
    site_id?: string;
    report_kind?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ReportJobListResponse> {
    const q = new URLSearchParams();
    if (params?.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params?.report_kind) q.set('report_kind', String(params.report_kind || '').trim());
    if (params?.status) q.set('status', String(params.status || '').trim());
    if (params?.limit != null && Number.isFinite(params.limit)) q.set('limit', String(Math.max(1, Number(params.limit) || 50)));
    if (params?.offset != null && Number.isFinite(params.offset)) q.set('offset', String(Math.max(0, Number(params.offset) || 0)));
    const qs = q.toString();
    const res = await api.get(`/api/v1/reports/jobs${qs ? `?${qs}` : ''}`);
    return res.data as ReportJobListResponse;
  },

  async getReportSreHealth(): Promise<ReportSreHealthResponse> {
    const res = await api.get('/api/v1/reports/sre/health');
    return res.data as ReportSreHealthResponse;
  },

  async getIncidentsRollup(params: IncidentsRollupFilters): Promise<IncidentsRollupResponse> {
    const qs = _buildRollupQuery(params);
    const res = await api.get(`/api/v1/reports/incidents/rollup?${qs}`);
    return res.data as IncidentsRollupResponse;
  },

  async downloadIncidentsRollupCsv(params: IncidentsRollupFilters): Promise<{ blob: Blob; filename: string }> {
    const qs = _buildRollupQuery(params);
    const res = await api.get(`/api/v1/reports/incidents/rollup.csv?${qs}`, { responseType: 'blob' });
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], 'incidents_rollup.csv');
    return { blob: res.data as Blob, filename };
  },

  async downloadReliabilityBundle(params: ReliabilityBundleFilters): Promise<{ blob: Blob; filename: string }> {
    const q = new URLSearchParams();
    q.set('start_ts', params.start_ts);
    q.set('end_ts', params.end_ts);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    q.set('scope', String(params.scope || 'site'));
    if (params.scope_id) q.set('scope_id', String(params.scope_id || '').trim());
    q.set('format', String(params.format || 'pdf'));
    const res = await api.get(`/api/v1/reports/reliability_bundle?${q.toString()}`, { responseType: 'blob' });
    const fmt = String(params.format || 'pdf').toLowerCase();
    const ext = fmt === 'json' ? 'json' : fmt === 'pdf' ? 'pdf' : 'zip';
    const fallback = `reliability_bundle_${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async downloadReliabilityProofPack(params: ReliabilityProofPackFilters): Promise<{ blob: Blob; filename: string }> {
    const q = new URLSearchParams();
    q.set('start_ts', params.start_ts);
    q.set('end_ts', params.end_ts);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params.device_id) q.set('device_id', String(params.device_id || '').trim());
    if (params.zone_id) q.set('zone_id', String(params.zone_id || '').trim());
    if (params.policy_type) q.set('policy_type', String(params.policy_type || '').trim());
    if (params.case_id) q.set('case_id', String(params.case_id || '').trim());
    if (params.incident_id) q.set('incident_id', String(params.incident_id || '').trim());
    for (const cameraId of params.camera_ids || []) {
      const trimmed = String(cameraId || '').trim();
      if (trimmed) q.append('camera_id', trimmed);
    }
    for (const policyId of params.policy_ids || []) {
      const trimmed = String(policyId || '').trim();
      if (trimmed) q.append('policy_id', trimmed);
    }
    const res = await api.get(`/api/v1/reports/reliability_proof_pack?${q.toString()}`, { responseType: 'blob' });
    const fallback = `incident_evidence_package_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async getReliabilityBundlePreview(params: ReliabilityBundleFilters): Promise<ReliabilityBundlePreviewResponse> {
    const q = new URLSearchParams();
    q.set('start_ts', params.start_ts);
    q.set('end_ts', params.end_ts);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    q.set('scope', String(params.scope || 'site'));
    if (params.scope_id) q.set('scope_id', String(params.scope_id || '').trim());
    q.set('format', 'json');
    const res = await api.get(`/api/v1/reports/reliability_bundle?${q.toString()}`, {
      timeout: 180000,
    });
    return res.data as ReliabilityBundlePreviewResponse;
  },

  async getReliabilityProofPackPreview(params: ReliabilityProofPackFilters): Promise<ReliabilityProofPackPreviewResponse> {
    const q = new URLSearchParams();
    q.set('start_ts', params.start_ts);
    q.set('end_ts', params.end_ts);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params.device_id) q.set('device_id', String(params.device_id || '').trim());
    if (params.zone_id) q.set('zone_id', String(params.zone_id || '').trim());
    if (params.policy_type) q.set('policy_type', String(params.policy_type || '').trim());
    if (params.case_id) q.set('case_id', String(params.case_id || '').trim());
    if (params.incident_id) q.set('incident_id', String(params.incident_id || '').trim());
    for (const cameraId of params.camera_ids || []) {
      const trimmed = String(cameraId || '').trim();
      if (trimmed) q.append('camera_id', trimmed);
    }
    for (const policyId of params.policy_ids || []) {
      const trimmed = String(policyId || '').trim();
      if (trimmed) q.append('policy_id', trimmed);
    }
    q.set('format', 'json');
    const res = await api.get(`/api/v1/reports/reliability_proof_pack?${q.toString()}`, {
      timeout: 45000,
    });
    if (res.status !== 202 && !res.data?.job?.job_id) {
      return res.data as ReliabilityProofPackPreviewResponse;
    }
    const jobId = String(res.data?.job?.job_id || '').trim();
    if (!jobId) {
      throw new Error('Proof pack async job did not return a job id.');
    }
    const startedAt = Date.now();
    const timeoutMs = 180000;
    while (Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
      const job = await reportsApi.getAsyncReportJob(jobId, { include_result: true });
      const status = String(job?.job?.status || '').trim().toLowerCase();
      if (status === 'completed' || status === 'partial') {
        return (job?.result || {}) as ReliabilityProofPackPreviewResponse;
      }
      if (status === 'failed' || status === 'canceled' || status === 'expired') {
        throw new Error(String(job?.job?.error || `Proof pack async job ${status}`));
      }
    }
    throw new Error('Proof pack moved to async generation and is still running. Keep this page open and check report jobs.');
  },

  async getEvidencePack(params: EvidencePackFilters): Promise<EvidencePackResponse> {
    const q = new URLSearchParams();
    if (params.from_date) q.set('from_date', params.from_date);
    if (params.to_date) q.set('to_date', params.to_date);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params.device_id) q.set('device_id', String(params.device_id || '').trim());
    if (params.zone_id) q.set('zone_id', String(params.zone_id || '').trim());
    if (params.policy_type) q.set('policy_type', String(params.policy_type || '').trim());
    if (params.case_id) q.set('case_id', String(params.case_id || '').trim());
    if (params.incident_id) q.set('incident_id', String(params.incident_id || '').trim());
    if (params.report_id) q.set('report_id', String(params.report_id || '').trim());
    if (params.hazard_class) q.set('hazard_class', String(params.hazard_class || '').trim());
    if (params.build === false) q.set('build', '0');
    for (const cameraId of params.camera_ids || []) {
      const trimmed = String(cameraId || '').trim();
      if (trimmed) q.append('camera_id', trimmed);
    }
    for (const policyId of params.policy_ids || []) {
      const trimmed = String(policyId || '').trim();
      if (trimmed) q.append('policy_id', trimmed);
    }
    q.set('format', 'json');
    const res = await api.get(`/api/v1/reports/evidence_pack?${q.toString()}`, {
      timeout: EVIDENCE_PACK_REQUEST_TIMEOUT_MS,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    });
    const initial = res.data as EvidencePackResponse;
    const isAsync =
      res.status === 202
      || String(initial?.mode || '').trim().toLowerCase() === 'async'
      || Boolean(initial?.job?.job_id);
    if (!isAsync) {
      return initial;
    }
    const jobId = String(initial?.job?.job_id || '').trim();
    if (!jobId) {
      throw new Error('Scan Package async job did not return a job id.');
    }
    // Poll the report job until the Scan Package completes (multi-day windows).
    const startedAt = Date.now();
    const timeoutMs = Math.max(EVIDENCE_PACK_REQUEST_TIMEOUT_MS, 900_000);
    while (Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
      const job = await reportsApi.getAsyncReportJob(jobId, { include_result: true });
      const status = String(job?.job?.status || '').trim().toLowerCase();
      if (status === 'completed' || status === 'partial') {
        const result = (job?.result || {}) as EvidencePackResponse;
        if (result && typeof result === 'object') {
          return result;
        }
        return { ok: false, error: 'evidence_pack_job_empty_result' };
      }
      if (status === 'failed' || status === 'canceled' || status === 'expired') {
        throw Object.assign(
          new Error(String(job?.job?.error || `Scan Package async job ${status}`)),
          {
            response: {
              status: Number(job?.job?.result_status_code || 500),
              data: {
                error: String(job?.job?.error || status),
                detail: String(job?.job?.error || `Scan Package async job ${status}`),
              },
            },
          },
        );
      }
    }
    throw Object.assign(
      new Error('Scan Package is still building. Keep this page open and retry in a moment.'),
      {
        code: 'ECONNABORTED',
        response: {
          status: 504,
          data: {
            error: 'gateway_timeout',
            detail: 'Scan Package is still building. Keep this page open and retry in a moment.',
          },
        },
      },
    );
  },

  async downloadEvidencePack(params: EvidencePackFilters): Promise<{ blob: Blob; filename: string }> {
    const q = new URLSearchParams();
    if (params.from_date) q.set('from_date', params.from_date);
    if (params.to_date) q.set('to_date', params.to_date);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params.device_id) q.set('device_id', String(params.device_id || '').trim());
    if (params.zone_id) q.set('zone_id', String(params.zone_id || '').trim());
    if (params.policy_type) q.set('policy_type', String(params.policy_type || '').trim());
    if (params.case_id) q.set('case_id', String(params.case_id || '').trim());
    if (params.incident_id) q.set('incident_id', String(params.incident_id || '').trim());
    if (params.report_id) q.set('report_id', String(params.report_id || '').trim());
    if (params.hazard_class) q.set('hazard_class', String(params.hazard_class || '').trim());
    for (const cameraId of params.camera_ids || []) {
      const trimmed = String(cameraId || '').trim();
      if (trimmed) q.append('camera_id', trimmed);
    }
    for (const policyId of params.policy_ids || []) {
      const trimmed = String(policyId || '').trim();
      if (trimmed) q.append('policy_id', trimmed);
    }
    q.set('format', 'zip');
    const res = await api.get(`/api/v1/reports/evidence_pack?${q.toString()}`, {
      responseType: 'blob',
      timeout: EVIDENCE_PACK_REQUEST_TIMEOUT_MS,
    });
    const fallback = `Safety_Report_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async downloadTrustPack(params: TrustPackFilters): Promise<{ blob: Blob; filename: string }> {
    const q = new URLSearchParams();
    q.set('from_date', params.from_date);
    q.set('to_date', params.to_date);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    q.set('format', 'zip');
    const res = await api.get(`/api/v1/reports/trust_pack?${q.toString()}`, { responseType: 'blob' });
    const fallback = `trust_pack_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async startTrustPackBuild(params: TrustPackFilters): Promise<TrustPackBuildStartResponse> {
    const q = new URLSearchParams();
    q.set('from_date', params.from_date);
    q.set('to_date', params.to_date);
    if (params.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params.site_id) q.set('site_id', String(params.site_id || '').trim());
    q.set('format', 'json');
    const res = await api.get(`/api/v1/reports/trust_pack?${q.toString()}`);
    return res.data as TrustPackBuildStartResponse;
  },

  async getTrustPackJobStatus(jobId: string): Promise<TrustPackJobStatusResponse> {
    const q = new URLSearchParams();
    q.set('job_id', String(jobId || '').trim());
    const res = await api.get(`/api/v1/reports/trust_pack/status?${q.toString()}`);
    return res.data as TrustPackJobStatusResponse;
  },

  async downloadTrustPackJob(jobId: string): Promise<{ blob: Blob; filename: string }> {
    const q = new URLSearchParams();
    q.set('job_id', String(jobId || '').trim());
    const res = await api.get(`/api/v1/reports/trust_pack/download?${q.toString()}`, { responseType: 'blob' });
    const fallback = `trust_pack_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async runCraReadinessJson(input: CraReadinessRunInput): Promise<CraReadinessJsonResponse> {
    const fmt = 'json';
    const fd = _buildCraFormData(input || {});
    const res = await api.post(`/api/v1/reports/cra/readiness?format=${fmt}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data as CraReadinessJsonResponse;
  },

  async runCraReadinessDownload(
    input: CraReadinessRunInput & { format: Exclude<CraReadinessFormat, 'json'> }
  ): Promise<{ blob: Blob; filename: string }> {
    const fmt = input?.format === 'pdf' ? 'pdf' : 'zip';
    const fd = _buildCraFormData(input || {});
    const res = await api.post(`/api/v1/reports/cra/readiness?format=${fmt}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    const fallback = `cra_report_${new Date().toISOString().replace(/[:.]/g, '-')}.${fmt}`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async runSiteReadinessJson(input: SiteReadinessRunInput): Promise<SiteReadinessJsonResponse> {
    const fmt = 'json';
    const fd = _buildSiteReadinessFormData(input || {});
    const res = await api.post(`/api/v1/reports/site/readiness?format=${fmt}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data as SiteReadinessJsonResponse;
  },

  async runSiteReadinessDownload(
    input: SiteReadinessRunInput & { format: Exclude<SiteReadinessFormat, 'json'> }
  ): Promise<{ blob: Blob; filename: string }> {
    const fmt = input?.format === 'pdf' ? 'pdf' : 'zip';
    const fd = _buildSiteReadinessFormData(input || {});
    const res = await api.post(`/api/v1/reports/site/readiness?format=${fmt}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    const fallback = `site_readiness_${new Date().toISOString().replace(/[:.]/g, '-')}.${fmt}`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async createOrResumeSafetyReportDraft(
    workflowId: string,
    input?: {
      workflow_anchor?: PersistedSafetyReportWorkflowAnchor;
      representative_incident_id?: string;
      scope?: PersistedSafetyReportWorkflowAnchor['scope'];
      time_window?: PersistedSafetyReportWorkflowAnchor['time_window'];
      report_payload?: Record<string, any>;
      created_from_queue_state_id?: string;
    }
  ): Promise<SafetyReportDraftMutationResponse> {
    const res = await api.post(
      `/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/report-draft`,
      input || {},
    );
    return res.data as SafetyReportDraftMutationResponse;
  },

  async saveSafetyFinding(
    workflowId: string,
    input?: {
      workflow_anchor?: PersistedSafetyReportWorkflowAnchor;
      representative_incident_id?: string;
      scope?: PersistedSafetyReportWorkflowAnchor['scope'];
      time_window?: PersistedSafetyReportWorkflowAnchor['time_window'];
      source_pattern_id?: string;
      source_group_key?: string;
      hazard_category?: string;
      finding_type?: string;
      saved_by?: string;
      followup_owner?: string | null;
      followup_unassigned?: boolean;
    }
  ): Promise<SafetyFindingMutationResponse> {
    const res = await api.post(
      `/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/finding`,
      input || {},
    );
    return res.data as SafetyFindingMutationResponse;
  },

  async listSafetyCases(params?: {
    include_archived?: boolean;
    safety_case_status?: string;
    workflow_id?: string;
    limit?: number;
  }): Promise<SafetyCaseListResponse> {
    const q = new URLSearchParams();
    if (params?.include_archived) q.set('include_archived', '1');
    if (params?.safety_case_status) q.set('safety_case_status', String(params.safety_case_status || '').trim());
    if (params?.workflow_id) q.set('workflow_id', String(params.workflow_id || '').trim());
    if (params?.limit != null && Number.isFinite(params.limit)) q.set('limit', String(Math.max(1, Number(params.limit) || 100)));
    const qs = q.toString();
    const res = await api.get(`/api/v1/safety-cases${qs ? `?${qs}` : ''}`);
    const data = res.data as SafetyCaseListResponse;
    const cases = Array.isArray(data.safety_cases)
      ? data.safety_cases
      : Array.isArray(data.items)
        ? data.items
        : [];
    return { ...data, safety_cases: cases, items: cases, count: cases.length } as SafetyCaseListResponse;
  },

  async openSafetyCase(safetyCaseId: string): Promise<SafetyCaseOpenResponse> {
    const res = await api.get(`/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}`);
    return res.data as SafetyCaseOpenResponse;
  },

  async listCaseTimeline(safetyCaseId: string): Promise<CaseTimelineResponse> {
    const res = await api.get(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/timeline`,
    );
    return res.data as CaseTimelineResponse;
  },

  async listControlEvaluations(safetyCaseId: string): Promise<ControlEvaluationsResponse> {
    const res = await api.get(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/control-evaluations`,
    );
    return res.data as ControlEvaluationsResponse;
  },

  async recordControlEvaluation(
    safetyCaseId: string,
    input?: Record<string, unknown>,
  ): Promise<ControlEvaluationsResponse> {
    const res = await api.post(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/control-evaluations`,
      input || {},
    );
    return res.data as ControlEvaluationsResponse;
  },

  async patchWorkplaceCondition(
    safetyCaseId: string,
    input: {
      condition_type?: string | null;
      condition_summary?: string | null;
      likely_contributing_condition?: string | null;
      contributing_condition_status?: string | null;
      condition_tags?: string[] | null;
      reviewer_notes?: string | null;
      input_needed_from?: string | null;
      why_this_matters_text?: string | null;
      worker_support_note_enabled?: boolean | null;
    },
  ): Promise<SafetyCaseOpenResponse> {
    const res = await api.patch(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/workplace-condition`,
      input,
    );
    return res.data as SafetyCaseOpenResponse;
  },

  async patchSafetyCaseOwner(
    safetyCaseId: string,
    input: { owner?: string | null; unassigned?: boolean; clear?: boolean },
  ): Promise<{
    ok: boolean;
    safety_case_id?: string;
    owner?: string | null;
    assigned_at?: string | null;
    assigned_by?: string | null;
    is_assigned?: boolean;
  }> {
    const res = await api.patch(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/owner`,
      input,
    );
    return res.data;
  },

  async patchClosureSummary(
    safetyCaseId: string,
    input: {
      what_was_observed?: string | null;
      what_changed?: string | null;
      what_followup_showed?: string | null;
      what_remains_open?: string | null;
      monitoring_note?: string | null;
    },
  ): Promise<SafetyCaseOpenResponse> {
    const res = await api.patch(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/closure-summary`,
      input,
    );
    return res.data as SafetyCaseOpenResponse;
  },

  async patchResultStatus(
    safetyCaseId: string,
    input: {
      result_status?: string | null;
    },
  ): Promise<SafetyCaseOpenResponse> {
    const res = await api.patch(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/result-status`,
      input,
    );
    return res.data as SafetyCaseOpenResponse;
  },

  async recordNewActivityDecision(
    safetyCaseId: string,
    input: {
      decision_kind: string;
      idempotency_key: string;
      reviewer_note?: string | null;
      representative_incident_id?: string | null;
      acknowledged_through?: string | null;
      frozen_signed_scan_id?: string | null;
    },
  ): Promise<NewActivityDecisionResponse> {
    const res = await api.post(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/new-activity-decisions`,
      input,
      { headers: { 'Idempotency-Key': input.idempotency_key } },
    );
    return res.data as NewActivityDecisionResponse;
  },

  async openSafetyCaseReport(safetyCaseId: string): Promise<SafetyCaseReportOpenResponse> {
    const res = await api.get(`/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/report`);
    return res.data as SafetyCaseReportOpenResponse;
  },

  async listSafetyCaseRecommendations(safetyCaseId: string): Promise<SafetyCaseRecommendationListResponse> {
    const res = await api.get(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/recommendations`,
    );
    return res.data as SafetyCaseRecommendationListResponse;
  },

  async createSafetyCaseRecommendation(
    safetyCaseId: string,
    input: {
      recommendation_text: string;
      recommendation_type?: string;
      priority?: string;
      control_level?: string;
      hierarchy_of_controls_level?: string;
      rationale?: string;
      reason?: string;
      generation_context?: Record<string, any>;
      created_by?: string;
    },
  ): Promise<SafetyCaseRecommendationMutationResponse> {
    const res = await api.post(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/recommendations`,
      input || {},
    );
    return res.data as SafetyCaseRecommendationMutationResponse;
  },

  async recordSafetyCaseRecommendationDecision(
    safetyCaseId: string,
    recommendationId: string,
    input: {
      decision?: SafetyCaseRecommendationDecisionState;
      decision_reason?: string;
      decision_notes?: string;
      customer_modified_recommendation_text?: string;
      corrected_text?: string;
      review_owner?: string;
      review_due_at?: string;
      decision_context?: Record<string, any>;
      decided_by?: string;
    },
  ): Promise<SafetyCaseRecommendationMutationResponse> {
    const res = await api.post(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/recommendations/${encodeURIComponent(String(recommendationId || '').trim())}/decision`,
      input || {},
    );
    return res.data as SafetyCaseRecommendationMutationResponse;
  },

  async recordSafetyCaseRecommendationCorrection(
    safetyCaseId: string,
    recommendationId: string,
    input: {
      customer_modified_recommendation_text?: string;
      corrected_text?: string;
      correction_reason?: string;
      constraint_type?: string;
      corrected_by?: string;
    },
  ): Promise<SafetyCaseRecommendationMutationResponse> {
    const res = await api.post(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/recommendations/${encodeURIComponent(String(recommendationId || '').trim())}/correction`,
      input || {},
    );
    return res.data as SafetyCaseRecommendationMutationResponse;
  },

  async createSafetyCaseRecommendationCorrectiveAction(
    safetyCaseId: string,
    recommendationId: string,
    input: {
      decision_reason?: string;
      decision_notes?: string;
      decided_by?: string;
      corrective_action: {
        title?: string;
        description?: string;
        assignee?: string;
        priority?: string;
        due_date?: string;
        action_type?: string;
        status?: string;
        implemented_at?: string;
      };
    },
  ): Promise<SafetyCaseRecommendationMutationResponse> {
    const res = await api.post(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/recommendations/${encodeURIComponent(String(recommendationId || '').trim())}/corrective-action/create`,
      input || {},
    );
    return res.data as SafetyCaseRecommendationMutationResponse;
  },

  async linkSafetyCaseRecommendationCorrectiveAction(
    safetyCaseId: string,
    recommendationId: string,
    input: {
      corrective_action_id: string;
      decision_reason?: string;
      decision_notes?: string;
      decided_by?: string;
      decision_context?: Record<string, any>;
    },
  ): Promise<SafetyCaseRecommendationMutationResponse> {
    const res = await api.post(
      `/api/v1/safety-cases/${encodeURIComponent(String(safetyCaseId || '').trim())}/recommendations/${encodeURIComponent(String(recommendationId || '').trim())}/corrective-action/link`,
      input || {},
    );
    return res.data as SafetyCaseRecommendationMutationResponse;
  },

  async getWorkflowClaimReadiness(workflowId: string, reportId?: string | null): Promise<WorkflowClaimReadinessResponse> {
    const query = reportId ? `?report_id=${encodeURIComponent(String(reportId).trim())}` : '';
    const res = await api.get(
      `/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/claim-readiness${query}`,
    );
    return res.data as WorkflowClaimReadinessResponse;
  },

  async createValidationWorkflow(
    workflowId: string,
    input?: {
      report_id?: string | null;
      safety_case_id?: string | null;
      title?: string;
      decision_readiness?: DecisionReadinessPayload | null;
      extra_blockers?: Array<{
        blocker_type?: string | null;
        source_label?: string | null;
        details?: string | null;
      }>;
    },
  ): Promise<ValidationWorkflowMutationResponse> {
    const res = await api.post(
      `/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/claim-readiness/validation-workflow`,
      input || {},
    );
    return res.data as ValidationWorkflowMutationResponse;
  },

  async patchValidationTask(
    taskId: string,
    input?: {
      status?: ValidationTaskStatus;
      assignee?: string;
      evidence_note?: string;
      waiver_reason?: string;
      control_payload?: Record<string, any>;
      result_payload?: Record<string, any>;
      due_date?: string;
    },
  ): Promise<ValidationTaskMutationResponse> {
    const res = await api.patch(
      `/api/v1/validation-tasks/${encodeURIComponent(String(taskId || '').trim())}`,
      input || {},
    );
    return res.data as ValidationTaskMutationResponse;
  },

  async listWorkflowCorrectiveActions(workflowId: string): Promise<WorkflowCorrectiveActionListResponse> {
    const res = await api.get(`/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/corrective-actions`);
    return res.data as WorkflowCorrectiveActionListResponse;
  },

  async listWorkflowFieldVerification(workflowId: string): Promise<import('../domain/incidents/fieldVerification').FieldVerificationSummary & { ok?: boolean; error?: string }> {
    const res = await api.get(`/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/field-verification`);
    return res.data as import('../domain/incidents/fieldVerification').FieldVerificationSummary & { ok?: boolean; error?: string };
  },

  async answerWorkflowFieldVerificationItem(
    workflowId: string,
    input: {
      item_key: string;
      disposition: string;
      note?: string;
      evidence_ref?: string;
      answered_by?: string;
      incident_id?: string;
      safety_case_id?: string;
    },
  ): Promise<import('../domain/incidents/fieldVerification').FieldVerificationSummary & { ok?: boolean; error?: string }> {
    const res = await api.post(
      `/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/field-verification/answers`,
      input || {},
    );
    return res.data as import('../domain/incidents/fieldVerification').FieldVerificationSummary & { ok?: boolean; error?: string };
  },

  async createWorkflowCorrectiveAction(
    workflowId: string,
    input?: {
      action_type?: string;
      title?: string;
      description?: string;
      priority?: string;
      assignee?: string;
      status?: WorkflowCorrectiveActionStatus;
      verification_method?: string;
      verification_notes?: string;
      safety_control_review?: SafetyControlReviewPayload | null;
      due_date?: string;
      implemented_at?: string;
    },
  ): Promise<WorkflowCorrectiveActionMutationResponse> {
    const res = await api.post(
      `/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/corrective-actions`,
      input || {},
    );
    return res.data as WorkflowCorrectiveActionMutationResponse;
  },

  async patchWorkflowCorrectiveAction(
    actionId: string,
    input?: {
      action_type?: string;
      title?: string;
      description?: string;
      priority?: string;
      assignee?: string;
      status?: WorkflowCorrectiveActionStatus;
      verification_method?: string;
      verification_notes?: string;
      safety_control_review?: SafetyControlReviewPayload | null;
      due_date?: string;
      implemented_at?: string;
    },
  ): Promise<WorkflowCorrectiveActionMutationResponse> {
    const res = await api.patch(
      `/api/v1/corrective-actions/${encodeURIComponent(String(actionId || '').trim())}`,
      input || {},
    );
    return res.data as WorkflowCorrectiveActionMutationResponse;
  },

  async addWorkflowCorrectiveActionEvidence(
    actionId: string,
    input?: {
      evidence_type?: WorkflowCorrectiveActionEvidenceType;
      description?: string;
      artifact_url?: string;
      metadata?: Record<string, any>;
      created_by?: string;
    },
  ): Promise<WorkflowCorrectiveActionMutationResponse> {
    const res = await api.post(
      `/api/v1/corrective-actions/${encodeURIComponent(String(actionId || '').trim())}/evidence`,
      input || {},
    );
    return res.data as WorkflowCorrectiveActionMutationResponse;
  },

  async getWorkflowImpactSummary(
    workflowId: string,
    options?: { reportWindowStart?: string | null; reportWindowEnd?: string | null },
  ): Promise<WorkflowImpactSummary & { ok: boolean; error?: string }> {
    const url = `/api/v1/workflows/${encodeURIComponent(String(workflowId || '').trim())}/impact-summary`;
    const params: Record<string, string> = {};
    const start = String(options?.reportWindowStart || '').trim();
    const end = String(options?.reportWindowEnd || '').trim();
    if (start) params.report_window_start = start;
    if (end) params.report_window_end = end;
    const res = Object.keys(params).length
      ? await api.get(url, { params })
      : await api.get(url);
    return res.data as WorkflowImpactSummary & { ok: boolean; error?: string };
  },

  async finalizeSafetyReport(
    reportId: string,
    input?: { expected_version?: number },
  ): Promise<SafetyReportDraftMutationResponse> {
    const body = Number.isFinite(Number(input?.expected_version))
      ? { expected_version: Number(input?.expected_version) }
      : {};
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/finalize`,
      body,
      { timeout: SAFETY_REPORT_FINALIZE_TIMEOUT_MS },
    );
    return res.data as SafetyReportDraftMutationResponse;
  },

  async deriveDraftFromSafetyReport(reportId: string): Promise<SafetyReportDraftMutationResponse> {
    const res = await api.post(`/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/derive-draft`, {});
    return res.data as SafetyReportDraftMutationResponse;
  },

  async regenerateSafetyReport(
    reportId: string,
    input?: { report_payload?: Record<string, any> }
  ): Promise<SafetyReportDraftMutationResponse> {
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/regenerate`,
      input || {},
    );
    return res.data as SafetyReportDraftMutationResponse;
  },

  async reanchorSafetyReport(
    reportId: string,
    input: {
      workflow_id?: string;
      workflow_anchor?: PersistedSafetyReportWorkflowAnchor;
      representative_incident_id?: string;
      scope?: PersistedSafetyReportWorkflowAnchor['scope'];
      time_window?: PersistedSafetyReportWorkflowAnchor['time_window'];
      created_from_queue_state_id?: string;
      reason: string;
    }
  ): Promise<SafetyReportDraftMutationResponse> {
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/reanchor`,
      input || {},
    );
    return res.data as SafetyReportDraftMutationResponse;
  },

  async openSafetyReport(reportId: string): Promise<SafetyReportOpenResponse> {
    const res = await api.get(`/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/open`);
    return res.data as SafetyReportOpenResponse;
  },

  async getOperatorLifecycle(reportId: string): Promise<OperatorLifecycleResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/operator-lifecycle${
        qs ? `?${qs}` : ''
      }`,
    );
    return res.data as OperatorLifecycleResponse;
  },

  async establishMeasurementReference(
    reportId: string,
    role: 'initial_baseline' | 'control_reference',
  ): Promise<OperatorLifecycleResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/measurement-references/establish${
        qs ? `?${qs}` : ''
      }`,
      { role },
    );
    return res.data as OperatorLifecycleResponse;
  },

  async getObservationDispositionQueue(
    reportId: string,
    opts?: { limit?: number },
  ): Promise<ObservationDispositionQueueResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    if (opts?.limit != null && Number.isFinite(opts.limit)) {
      q.set('limit', String(Math.max(1, Math.trunc(opts.limit))));
    }
    const qs = q.toString();
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/observation-disposition-queue${
        qs ? `?${qs}` : ''
      }`,
    );
    return res.data as ObservationDispositionQueueResponse;
  },

  async getScanVerification(reportId: string): Promise<ScanVerificationResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/verification${
        qs ? `?${qs}` : ''
      }`,
    );
    return res.data as ScanVerificationResponse;
  },

  async startScanVerification(reportId: string): Promise<ScanVerificationResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/verification/freeze${
        qs ? `?${qs}` : ''
      }`,
      {},
    );
    return res.data as ScanVerificationResponse;
  },

  async reconcileScanVerification(reportId: string): Promise<ScanMeasurementReconciliation> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/verification/reconcile${
        qs ? `?${qs}` : ''
      }`,
      {},
    );
    return res.data as ScanMeasurementReconciliation;
  },

  async recordScanVerificationDisposition(
    reportId: string,
    memberId: string,
    body: ScanVerificationDispositionRequest,
  ): Promise<ScanVerificationResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/verification/items/${encodeURIComponent(String(memberId || '').trim())}/dispositions${
        qs ? `?${qs}` : ''
      }`,
      body,
    );
    return res.data as ScanVerificationResponse;
  },

  async adoptScanVerificationProposal(
    reportId: string,
    memberId: string,
    body: ScanVerificationAdoptRequest,
  ): Promise<ScanVerificationResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/verification/items/${encodeURIComponent(String(memberId || '').trim())}/adopt-proposal${
        qs ? `?${qs}` : ''
      }`,
      body,
    );
    return res.data as ScanVerificationResponse;
  },

  async adoptRemainingScanVerificationProposals(
    reportId: string,
    body: ScanVerificationAdoptRequest,
  ): Promise<ScanVerificationResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/verification/adopt-remaining-proposals${
        qs ? `?${qs}` : ''
      }`,
      body,
    );
    return res.data as ScanVerificationResponse;
  },

  async getMeasurementAssurance(
    reportId: string,
    params?: { evaluation_run_id?: string; usable_seconds?: number },
  ): Promise<MeasurementAssuranceResponse> {
    // Hosted tenant scope requires org_id/site_id as query params (headers are ignored in prod).
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    if (params?.evaluation_run_id) {
      q.set('evaluation_run_id', String(params.evaluation_run_id).trim());
    }
    if (params?.usable_seconds != null && Number.isFinite(params.usable_seconds)) {
      q.set('usable_seconds', String(Math.max(0, Math.trunc(Number(params.usable_seconds)))));
    }
    const qs = q.toString();
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/measurement-assurance${
        qs ? `?${qs}` : ''
      }`,
    );
    return res.data as MeasurementAssuranceResponse;
  },

  async admitReportEvaluation(
    reportId: string,
    input: {
      evaluation_run_id: string;
      admit?: boolean;
      supplements?: Record<string, unknown>;
    },
  ): Promise<AdmitReportEvaluationResponse> {
    const scope = ensureUiScope();
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    const qs = q.toString();
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/admit-evaluation${
        qs ? `?${qs}` : ''
      }`,
      {
        evaluation_run_id: String(input.evaluation_run_id || '').trim(),
        admit: input.admit !== false,
        supplements: input.supplements && typeof input.supplements === 'object' ? input.supplements : {},
        org_id: scope.orgId || undefined,
        site_id: scope.siteId || undefined,
      },
    );
    return res.data as AdmitReportEvaluationResponse;
  },

  async getSafetyReportArtifact(reportId: string): Promise<SafetyReportArtifactDetailResponse> {
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact?view=page`,
      { timeout: 240000 },
    );
    return res.data as SafetyReportArtifactDetailResponse;
  },

  async getSafetyReportArtifactStatus(
    reportId: string,
    options?: { face?: 'customer_compact' | 'detailed' | null; packageMode?: string | null },
  ): Promise<{
    ok: boolean;
    report_id?: string;
    status?: 'NOT_GENERATED' | 'GENERATING' | 'READY' | 'FAILED' | string;
    pdf?: { status?: string; artifact_key?: string };
    zip?: { status?: string; artifact_key?: string };
    error?: string;
  }> {
    const params = new URLSearchParams();
    const face = String(options?.face || '').trim();
    if (face === 'customer_compact' || face === 'detailed') params.set('face', face);
    const packageMode = String(options?.packageMode || '').trim();
    if (packageMode) params.set('package_mode', packageMode);
    const query = params.toString();
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/status${query ? `?${query}` : ''}`,
    );
    return res.data;
  },

  async generateSafetyReportArtifact(
    reportId: string,
    artifact: 'pdf' | 'zip',
  ): Promise<{ blob?: Blob; filename?: string } & Record<string, unknown>> {
    const res = await api.post(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/generate`,
      { artifact },
      { responseType: 'blob', timeout: 300000 },
    );
    return res.data;
  },

  async getSignedSafetyControlRecord(
    reportId: string,
    revision: number,
    scope?: { orgId?: string | null; siteId?: string | null },
  ): Promise<{ ok: boolean; record?: SignedSafetyControlRecord; error?: string }> {
    const params = new URLSearchParams({ revision: String(revision) });
    if (scope?.orgId) params.set('org_id', scope.orgId);
    if (scope?.siteId) params.set('site_id', scope.siteId);
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(reportId)}/signed-control-record?${params}`,
    );
    return res.data;
  },

  async getSignedSafetyControlRecordEvidence(
    reportId: string,
    revision: number,
    scope?: { orgId?: string | null; siteId?: string | null },
  ): Promise<SignedSafetyControlRecordEvidenceResponse> {
    const params = new URLSearchParams({ revision: String(revision) });
    if (scope?.orgId) params.set('org_id', scope.orgId);
    if (scope?.siteId) params.set('site_id', scope.siteId);
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(reportId)}/signed-control-record/evidence?${params}`,
    );
    return res.data as SignedSafetyControlRecordEvidenceResponse;
  },

  async fetchSignedSafetyControlRecordEvidenceMedia(mediaUri: string): Promise<Blob> {
    const uri = String(mediaUri || '').trim();
    if (!uri.includes('/signed-control-record/evidence/') || !uri.includes('/media?')) {
      throw new Error('invalid_signed_evidence_media_uri');
    }
    const res = await api.get(uri, { responseType: 'blob' });
    return res.data as Blob;
  },

  async downloadSignedSafetyControlRecordPdf(
    reportId: string,
    revision: number,
    scope?: { orgId?: string | null; siteId?: string | null },
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams();
    if (scope?.orgId) params.set('org_id', scope.orgId);
    if (scope?.siteId) params.set('site_id', scope.siteId);
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(reportId)}/signed-control-record/revisions/${revision}/download/pdf${
        params.toString() ? `?${params}` : ''
      }`,
      { responseType: 'blob', timeout: 240000 },
    );
    const blob = res.data as Blob;
    const sample = blob.slice(0, 5);
    const headBuf =
      typeof sample.arrayBuffer === 'function'
        ? await sample.arrayBuffer()
        : await new Response(sample).arrayBuffer();
    const head = new Uint8Array(headBuf);
    let magic = '';
    for (let i = 0; i < head.length; i += 1) {
      magic += String.fromCharCode(head[i]);
    }
    if (!magic.startsWith('%PDF')) {
      throw new Error('signed_record_pdf_invalid_payload');
    }
    const fallback = `edgentik-signed-safety-control-record_${String(reportId || '').trim() || 'record'}_rev${revision}.pdf`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob, filename };
  },

  async getExternalSafetyReviewArtifact(reportId: string): Promise<Record<string, unknown>> {
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact?view=page`,
      { timeout: 240000 },
    );
    return res.data as Record<string, unknown>;
  },

  async downloadExternalSafetyScanPackage(reportId: string): Promise<{ blob: Blob; filename: string }> {
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/download?package_mode=external_safe`,
      { responseType: 'blob', timeout: 240000 },
    );
    const fallback = `safety_scan_package_${String(reportId || '').trim() || 'report'}.zip`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async downloadExternalSafetyScanPdf(
    reportId: string,
    options?: { rev?: number | null },
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams({ package_mode: 'external_safe' });
    if (options?.rev != null && Number(options.rev) >= 1) {
      params.set('rev', String(Math.floor(Number(options.rev))));
    }
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/download/pdf?${params}`,
      { responseType: 'blob', timeout: 240000 },
    );
    const fallback = `edgentik_signed_safety_scan_${String(reportId || '').trim() || 'report'}.pdf`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async downloadSafetyReportArtifact(
    reportId: string,
    options?: { rev?: number | null },
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams();
    if (options?.rev != null && Number(options.rev) >= 1) {
      params.set('rev', String(Math.floor(Number(options.rev))));
    }
    const query = params.toString();
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/download${query ? `?${query}` : ''}`,
      { responseType: 'blob', timeout: 300000 },
    );
    const fallback = `safety_report_artifact_${String(reportId || '').trim() || 'report'}.zip`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async getCustomerSafetyScanHtml(
    reportId: string,
    options?: {
      rev?: number | null;
      orgId?: string | null;
      siteId?: string | null;
    },
  ): Promise<string> {
    const params = new URLSearchParams({ format: 'html' });
    if (options?.rev != null && Number(options.rev) >= 1) {
      params.set('rev', String(Math.floor(Number(options.rev))));
    }
    if (options?.orgId) params.set('org_id', String(options.orgId));
    if (options?.siteId) params.set('site_id', String(options.siteId));
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/customer-safety-scan?${params}`,
      { responseType: 'text', timeout: 240000 },
    );
    return String(res.data || '');
  },

  async downloadSafetyReportArtifactPdf(
    reportId: string,
    options?: {
      rev?: number | null;
      /** customer_compact (Customer Safety Scan) or detailed (technical/legacy v1) */
      face?: 'customer_compact' | 'detailed' | null;
    },
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams();
    if (options?.rev != null && Number(options.rev) >= 1) {
      params.set('rev', String(Math.floor(Number(options.rev))));
    }
    const face = String(options?.face || '').trim();
    if (face === 'customer_compact' || face === 'detailed') {
      params.set('face', face);
    }
    const query = params.toString();
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/download/pdf${query ? `?${query}` : ''}`,
      { responseType: 'blob', timeout: 240000 },
    );
    const fallback = face === 'detailed'
      ? `edgentik-technical-report_${String(reportId || '').trim() || 'report'}.pdf`
      : `edgentik-customer-safety-scan_${String(reportId || '').trim() || 'report'}.pdf`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async downloadSafetyReportArtifactDocx(reportId: string): Promise<{ blob: Blob; filename: string }> {
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/download/docx`,
      { responseType: 'blob' },
    );
    const fallback = `safety_report_artifact_${String(reportId || '').trim() || 'report'}.docx`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async downloadSafetyReportArtifactCsv(reportId: string): Promise<{ blob: Blob; filename: string }> {
    const res = await api.get(
      `/api/v1/reports/${encodeURIComponent(String(reportId || '').trim())}/artifact/download/csv`,
      { responseType: 'blob' },
    );
    const fallback = `safety_report_artifact_${String(reportId || '').trim() || 'report'}.csv`;
    const filename = _filenameFromContentDisposition(res.headers?.['content-disposition'], fallback);
    return { blob: res.data as Blob, filename };
  },

  async listWorkflowReportCandidates(params?: {
    org_id?: string;
    site_id?: string;
    limit?: number;
  }): Promise<WorkflowReportCandidatesResponse> {
    const q = new URLSearchParams();
    if (params?.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params?.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params?.limit != null && Number.isFinite(params.limit)) q.set('limit', String(Math.max(1, Number(params.limit) || 25)));
    const qs = q.toString();
    const res = await api.get(`/api/v1/workflows/report-candidates${qs ? `?${qs}` : ''}`);
    return res.data as WorkflowReportCandidatesResponse;
  },

  async getSafetyDashboard(params?: {
    org_id?: string;
    site_id?: string;
    limit?: number;
    report_from?: string;
    report_to?: string;
  }): Promise<SafetyDashboardResponse> {
    const q = new URLSearchParams();
    if (params?.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params?.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params?.limit != null && Number.isFinite(params.limit)) q.set('limit', String(Math.max(1, Number(params.limit) || 25)));
    if (params?.report_from) q.set('from_date', String(params.report_from || '').trim());
    if (params?.report_to) q.set('to_date', String(params.report_to || '').trim());
    const qs = q.toString();
    const res = await api.get(`/api/v1/workflows/safety-dashboard${qs ? `?${qs}` : ''}`);
    return res.data as SafetyDashboardResponse;
  },

  async getSafetyPortfolio(params?: {
    org_id?: string;
    site_id?: string | string[];
    limit?: number;
    report_from?: string;
    report_to?: string;
  }): Promise<SafetyPortfolioResponse> {
    const q = new URLSearchParams();
    if (params?.org_id) q.set('org_id', String(params.org_id || '').trim());
    const siteIds = Array.isArray(params?.site_id)
      ? params?.site_id || []
      : params?.site_id
        ? [params.site_id]
        : [];
    siteIds.forEach((siteId) => {
      const trimmed = String(siteId || '').trim();
      if (trimmed) q.append('site_id', trimmed);
    });
    if (params?.limit != null && Number.isFinite(params.limit)) q.set('limit', String(Math.max(1, Number(params.limit) || 25)));
    if (params?.report_from) q.set('from_date', String(params.report_from || '').trim());
    if (params?.report_to) q.set('to_date', String(params.report_to || '').trim());
    const qs = q.toString();
    const res = await api.get(`/api/v1/workflows/safety-portfolio${qs ? `?${qs}` : ''}`);
    return res.data as SafetyPortfolioResponse;
  },

  async getActiveIncidentsRulePolicy(params?: { org_id?: string; site_id?: string; device_id?: string }): Promise<IncidentsRulePolicyActiveResponse> {
    const q = new URLSearchParams();
    if (params?.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params?.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params?.device_id) q.set('device_id', String(params.device_id || '').trim());
    const qs = q.toString();
    const res = await api.get(`/api/v1/reports/incidents/rule_policy/active${qs ? `?${qs}` : ''}`);
    return res.data as IncidentsRulePolicyActiveResponse;
  },

  async listIncidentsRulePolicyVersions(params?: {
    org_id?: string;
    site_id?: string;
    device_id?: string;
    limit?: number;
  }): Promise<IncidentsRulePolicyVersionsResponse> {
    const q = new URLSearchParams();
    if (params?.org_id) q.set('org_id', String(params.org_id || '').trim());
    if (params?.site_id) q.set('site_id', String(params.site_id || '').trim());
    if (params?.device_id) q.set('device_id', String(params.device_id || '').trim());
    if (params?.limit != null && Number.isFinite(params.limit)) q.set('limit', String(Math.max(1, Number(params.limit) || 50)));
    const qs = q.toString();
    const res = await api.get(`/api/v1/reports/incidents/rule_policy${qs ? `?${qs}` : ''}`);
    return res.data as IncidentsRulePolicyVersionsResponse;
  },

  async saveIncidentsRulePolicy(input: IncidentsRulePolicySaveInput): Promise<IncidentsRulePolicyActiveResponse> {
    const q = new URLSearchParams();
    if (input?.org_id) q.set('org_id', String(input.org_id || '').trim());
    if (input?.site_id) q.set('site_id', String(input.site_id || '').trim());
    const payload: any = {
      policy: input?.policy || {},
      activate: input?.activate !== false,
    };
    if (input?.version != null && Number.isFinite(input.version)) payload.version = Number(input.version);
    if (input?.created_by) payload.created_by = String(input.created_by || '').trim();
    const qs = q.toString();
    const res = await api.post(`/api/v1/reports/incidents/rule_policy${qs ? `?${qs}` : ''}`, payload);
    return res.data as IncidentsRulePolicyActiveResponse;
  },

  async activateIncidentsRulePolicyVersion(input: {
    org_id?: string;
    site_id?: string;
    version: number;
  }): Promise<IncidentsRulePolicyActiveResponse> {
    const q = new URLSearchParams();
    if (input?.org_id) q.set('org_id', String(input.org_id || '').trim());
    if (input?.site_id) q.set('site_id', String(input.site_id || '').trim());
    const payload = { version: Number(input?.version || 0) };
    const qs = q.toString();
    const res = await api.post(`/api/v1/reports/incidents/rule_policy/activate${qs ? `?${qs}` : ''}`, payload);
    return res.data as IncidentsRulePolicyActiveResponse;
  },

  async deactivateIncidentsRulePolicy(input?: {
    org_id?: string;
    site_id?: string;
  }): Promise<{
    ok: boolean;
    org_id?: string;
    site_id?: string;
    deactivated_count?: number;
    active_version?: number;
    active_from_db?: boolean;
    policy?: IncidentsRulePolicyDoc;
  }> {
    const q = new URLSearchParams();
    if (input?.org_id) q.set('org_id', String(input.org_id || '').trim());
    if (input?.site_id) q.set('site_id', String(input.site_id || '').trim());
    const qs = q.toString();
    const res = await api.post(`/api/v1/reports/incidents/rule_policy/deactivate${qs ? `?${qs}` : ''}`, {});
    return res.data as {
      ok: boolean;
      org_id?: string;
      site_id?: string;
      deactivated_count?: number;
      active_version?: number;
      active_from_db?: boolean;
      policy?: IncidentsRulePolicyDoc;
    };
  },

  async previewIncidentsRulePolicy(input: IncidentsRulePolicyPreviewInput): Promise<IncidentsRulePolicyPreviewResponse> {
    const q = new URLSearchParams();
    if (input?.org_id) q.set('org_id', String(input.org_id || '').trim());
    if (input?.site_id) q.set('site_id', String(input.site_id || '').trim());
    const payload: any = {
      policy: input?.policy || {},
      from_date: input?.from_date,
      to_date: input?.to_date,
      device_id: input?.device_id || undefined,
      zone_id: input?.zone_id || undefined,
      camera_ids: Array.isArray(input?.camera_ids) ? input.camera_ids : [],
      policy_ids: Array.isArray(input?.policy_ids) ? input.policy_ids : [],
      policy_type: input?.policy_type || undefined,
    };
    const qs = q.toString();
    const res = await api.post(`/api/v1/reports/incidents/rule_policy/preview${qs ? `?${qs}` : ''}`, payload);
    return res.data as IncidentsRulePolicyPreviewResponse;
  },
};
